/**
 * Onix Cart — main drawer logic
 * ─────────────────────────────
 * Owns `initCartDrawer`, the heart of the live-cart experience: rendering,
 * line updates, quantity stepper, bundle injection, open/close animation.
 *
 * Shared utilities (escapeHtml, loggedFetch, showCartError) live on the
 * `window.OnixCart` namespace defined in cart-drawer-utils.js, which MUST
 * load before this file. The outer IIFE aliases them locally so the rest
 * of this file is unchanged vs. the pre-split version.
 *
 * Sibling scripts:
 *   - cart-drawer-quickadd.js  → the "+" popup on product cards
 *   - cart-drawer-discount.js  → the discount code input handler
 *
 * Load order in layout/theme.liquid: utils → this → quickadd → discount
 */
(() => {
  const OnixCart = window.OnixCart || {};
  const escapeHtml = OnixCart.escapeHtml || ((s) => String(s || ''));
  const loggedFetch = OnixCart.loggedFetch || fetch.bind(window);
  const showCartError = OnixCart.showCartError || ((m) => console.warn('[onix-cart]', m));

  const initCartDrawer = () => {
    const drawerRoot = document.querySelector("[data-cart-drawer-root]");
    if (!drawerRoot) return;

    if (drawerRoot.dataset.cartDrawerBound === "true") return;
    drawerRoot.dataset.cartDrawerBound = "true";

    /* Translation strings from data-t-* attributes */
    const t = {
      remove: drawerRoot.dataset.tRemove || 'Remove',
      quantity: drawerRoot.dataset.tQuantity || 'Quantity controls',
      addToCart: drawerRoot.dataset.tAddToCart || 'ADD TO CART',
      apply: drawerRoot.dataset.tApply || 'Apply',
      alreadyUsed: drawerRoot.dataset.tAlreadyUsed || 'Already used',
      error: drawerRoot.dataset.tError || 'Error',
      /* message de l'AJOUT raté — distinct de celui du champ de code (30/08) */
      addError: drawerRoot.dataset.tAddError || "Ajout impossible — réessaie.",
      chooseSize: drawerRoot.dataset.tChooseSize || 'Choose your size:',
      flocage: drawerRoot.dataset.tFlocage || 'Flocage',
      inStock: drawerRoot.dataset.tInStock || 'In stock',
      bundleLabel: drawerRoot.dataset.tBundleLabel || 'Offre Découverte',
      /* ⚠️ Deux libellés SÉPARÉS de ceux de l'offre découverte : cinq langues
         sont publiées sur cette boutique, et un texte français en dur se lit
         tel quel par un client allemand. Le repli n'est qu'un filet. */
      packLabel: drawerRoot.dataset.tPackLabel || 'Pack 2 vestes',
      packSave: drawerRoot.dataset.tPackSave || 'Tu économises @@',
      promoLabel: drawerRoot.dataset.tPromoLabel || 'Promo',
      quantityDown: drawerRoot.dataset.tQuantityDown || 'Retirer un',
    };

  /* ⛔ LE FLOCAGE SUR UNE SEULE LIGNE — 30/08, 06h56. Son choix : la version B.
       Avant : un encadré bleu, bordure gauche, trois lignes en majuscules —
       « NOM : RODRIGO / NUMÉRO : 23 / NOM SOUS NUMÉRO : CR7 ». 65 px de haut
       pour trois informations que le client vient lui-même de saisir.
       Sa remarque : « un truc que j'aime pas c'est l'affichage des flocages
       dans le panier, fais un truc plus adapté, plus organique, plus esthétique ».

     HUIT APPROCHES lui ont été soumises en grille, chacune montrée avec TROIS
     longueurs de flocage — court (CR7), moyen (RODRIGO) et le maximum que le
     formulaire autorise (20 caractères). C'est la colonne « max » qui départage :
     trois approches y doublaient ou triplaient de hauteur.

     ⚠️ CELLE-CI EN FAIT PARTIE, ET IL LE SAIT. Avec un nom de 16-20 caractères,
       la ligne passe sur deux ou trois lignes et le panier se réorganise sous les
       doigts du client. Je le lui ai dit avant qu'il choisisse ; il veut la voir
       en vrai avant de trancher. C'est le bon ordre — un rendu se juge sur
       l'écran, pas sur un tableau de hauteurs.

     ⚠️ LES LIBELLÉS DISPARAISSENT, ET C'EST LE POINT. « NOM : », « NUMÉRO : »,
       « NOM SOUS NUMÉRO : » occupaient plus de place que les valeurs elles-mêmes.
       Le client vient de remplir ces champs : il reconnaît son nom et son numéro
       sans qu'on les lui étiquette. On garde l'ORDRE de saisie, qui suffit à
       lever l'ambiguïté.
     ⚠️ L'ordre vient de `Object.entries`, donc de l'ordre d'insertion des
       propriétés — celui du formulaire. Le nom d'abord, le numéro ensuite. */
  function flocageEnLigne(props) {
    if (!props) return '';
    const vals = Object.entries(props)
      .filter(([k, v]) => !k.startsWith('_') && v !== '' && v != null)
      .map(([, v]) => String(v));
    if (!vals.length) return '';
    const dernier = vals.length > 1 ? vals.pop() : null;
    const forts = vals.map(v => `<b>${escapeHtml(v)}</b>`).join('<i aria-hidden="true">·</i>');
    return `<p class="onix-cart-item__floc">${forts}` +
      (dernier ? `<i aria-hidden="true">·</i><span>${escapeHtml(dernier)}</span>` : '') + `</p>`;
  }

    const drawerPanel = drawerRoot.querySelector(".onix-cart-drawer__panel");
    /* ─────────────────────────────────────────────────────────────────────
       REMPLACER LE CORPS SANS QUE LES LIGNES SPAWNENT
       Tous les rendus passent par `drawerBody.innerHTML = …`, qui DÉTRUIT puis
       RECRÉE les nœuds. Une transition CSS ne survit pas à ça : les anciens
       éléments n'existent plus, les nouveaux naissent à leur état final. Un
       `.onix-cart-item` a beau porter `transition: opacity .32s`, elle ne se
       déclenche jamais — il n'y a pas d'état de départ à quitter.

       ⚠️ ET CE N'EST PAS LA HAUTEUR DU CORPS QU'IL FAUT ANIMER. Premier essai,
       le 23/08 : un FLIP sur `drawerBody`. Sans effet — le corps est scrollable,
       sa hauteur rendue est CONTRAINTE (115 px, mesuré, identique avant/après).
       C'est son CONTENU qui change. Animer la boîte revenait à animer ce qui ne
       bougeait pas.

       ⚠️ ET ON N'ANIME QUE CE QUI EST NOUVEAU. Faire entrer toutes les lignes
       en fondu à chaque rendu ferait clignoter le panier entier au moindre
       changement de quantité. On compare donc les CLÉS Shopify d'avant et
       d'après : seule une ligne dont la clé n'était pas là entre en scène. Une
       ligne qui passe de 1× à 2× reste en place — c'est son contenu qui change,
       pas son existence. */
    const clesDuCorps = (el) => new Set(
      [...el.querySelectorAll('[data-cart-item-key]')]
        .map((n) => n.dataset.cartItemKey).filter(Boolean)
    );

    const remplacerCorps = (el, html) => {
      if (!el) return;
      const avant = clesDuCorps(el);
      const premierRendu = avant.size === 0;
      el.innerHTML = html;
      if (premierRendu) return;                 /* rien à comparer : pas d'entrée */

      const neuves = [...el.querySelectorAll('[data-cart-item-key]')]
        .filter((n) => !avant.has(n.dataset.cartItemKey));
      if (!neuves.length) return;

      neuves.forEach((n) => {
        n.style.opacity = '0';
        n.style.transform = 'translateY(10px)';
      });
      void el.offsetHeight;                     /* reflow : sans lui, pas d'animation */
      neuves.forEach((n, i) => {
        /* Un léger décalage entre lignes : elles arrivent l'une après l'autre
           plutôt qu'en bloc, ce qui se lit comme un mouvement et non comme un
           changement d'écran. Plafonné pour que le 5e article ne se fasse pas
           attendre. */
        n.style.transition = 'opacity 260ms cubic-bezier(0.4, 0, 0.2, 1) ' + Math.min(i * 45, 180) + 'ms, '
                           + 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1) ' + Math.min(i * 45, 180) + 'ms';
        n.style.opacity = '';
        n.style.transform = '';
        setTimeout(() => { n.style.transition = ''; }, 520);
      });
    };

    const drawerBody = drawerRoot.querySelector("[data-cart-drawer-body]");
    const subtotalNode = drawerRoot.querySelector("[data-cart-subtotal]");
    const cartCountNodes = document.querySelectorAll("[data-cart-count]");
    const cartToggleButtons = document.querySelectorAll("[data-cart-toggle]");
    const closeButtons = drawerRoot.querySelectorAll("[data-cart-close]");
    const currencyCode = drawerRoot.dataset.cartCurrency || "USD";
    /* Locale-aware cart root. routes.root_url (=> data-cart-root) is "/en"
       WITHOUT a trailing slash on non-default locales, so `${rootPath}cart.js`
       became "/encart.js" (404) and broke add/refresh in EN/DE/etc. We prefer
       window.Shopify.routes.root (correctly "/en/") and normalize to exactly
       one trailing slash. FR (default) => "/". */
    const rootPath = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || drawerRoot.dataset.cartRoot || "/").replace(/\/?$/, "/");
    const shouldOpenFromStorage = (() => {
      try {
        return window.sessionStorage.getItem("onixOpenCart") === "1";
      } catch (error) {
        return false;
      }
    })();

    /* EUR: format FR exact "24,99€" (virgule, sans espace) pour matcher `| money`.
       Autres devises: format natif via Intl (symbole-avant + point), identique
       au rendu présentment Shopify `| money` (ex. "£22.00", "$22.00", "CHF 22.00").
       narrowSymbol + locale 'en' = même sortie que `| money` quelle que soit la langue. */
    const formatMoney = (cents) => {
      const amount = (cents || 0) / 100;
      if (currencyCode === 'EUR') return amount.toFixed(2).replace('.', ',') + '€';
      try {
        return new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' }).format(amount);
      } catch (e) {
        try { return new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode }).format(amount); }
        catch (e2) { return amount.toFixed(2) + ' ' + currencyCode; }
      }
    };

    /* Nomme un code de palier par CE QU'IL A FALLU FAIRE pour l'obtenir.
       `onix4` → « Remise 3 maillots ». Rend null pour tout code inconnu — un
       code de collab, la roue — et l'appelant retombe alors sur le libellé
       générique. La correspondance vient de `_tiers`, donc de `data-tiers` :
       même source que l'échelle et la jauge, aucune liste recopiée. */
    /* Même traitement que l'échelle : les centimes nuls sautent, « 35,00€ »
       devient « 35€ ». Sans ça l'échelle affichait « -35€ » et la ligne juste
       en dessous « -35,00€ » — deux écritures du même montant à 20 px d'écart.
       La regex n'enlève que deux zéros collés au séparateur : -7,49€ est intact.
       ⚠️ DEPUIS LE 03/09 les paliers 3 à 5 sont des POURCENTAGES : le montant
         calculé tombe rarement rond (24,99 € · 34,98 € · 69,72 €), donc cette
         coupe ne s'applique presque plus qu'aux paliers 1 et 2. Elle reste : le
         jour où un panier donne un compte rond, les deux écritures divergeraient
         de nouveau. */
    /* ═══ LE TITRE EN DEUX LIGNES — variante « T3 », 18/08 ═══════════════
       Convention du catalogue, documentée dans snippets/product-card-title.liquid :
       tout titre vaut « ÉQUIPE - PRODUIT - COULEUR », séparateur « - » ou « – »,
       et LA COULEUR EST TOUJOURS LE DERNIER SEGMENT. On le jette, on garde le
       premier segment comme club et le reste comme sous-titre.

       ⚠️ C'EST UN JUMEAU, PAS UNE SECONDE RÈGLE. Le tiroir construit ses cartes
         en JS depuis /cart.js, donc le snippet Liquid ne lui est pas accessible.
         Si la convention de nommage change, LES DEUX se corrigent — celui-ci et
         `product-card-title.liquid`. Aucun autre endroit ne découpe un titre.
       ⚠️ « Maillot » saute en tête de sous-titre : « Maillot Third 2026/27 »
         devient « Third 2026/27 ». Sur 241 px de colonne, ce mot est le seul
         qui n'apprend rien — tout est un maillot dans cette boutique. */
    const titreEnDeux = (titre) => {
      const parts = String(titre || '').replace(/ – /g, ' - ').split(' - ').map(x => x.trim()).filter(Boolean);
      if (!parts.length) return { club: String(titre || ''), reste: '' };
      const garder = parts.length > 1 ? parts.slice(0, -1) : parts;
      const club = garder[0];
      const reste = garder.slice(1).join(' · ').replace(/^Maillot\s+/i, '');
      return { club, reste };
    };

    /* ⚠️ EXPOSÉ EXPRÈS. cart-drawer-second-jersey.js affiche la même carte
       avec le même titre sur deux lignes. En recopier la logique en ferait
       une TROISIÈME source — après ce fichier et snippets/product-card-title.liquid —
       et les trois divergeraient au premier titre exotique. */
    window.__onixTitreEnDeux = titreEnDeux;

    const sansCentimesNuls = (m) => String(m).replace(/([.,])00(?=\D*$)/, '');

    const libellePalier = (code) => {
      if (!code || !_tiers || !_tiers.length) return null;
      const c = String(code).toUpperCase();
      const t = _tiers.find(x => String(x.code).toUpperCase() === c);
      return t ? 'Remise ' + t.qty + ' maillot' + (t.qty > 1 ? 's' : '') : null;
    };

    const __lastValues = { subtotal: 0, compare: 0, itemsSubtotal: 0, count: 0, checkout: 0, checkoutCompare: 0 };

    /* ── Remise de palier OPTIMISTE ──────────────────────────────────────
       Pour que le TOTAL REMISÉ se mette à jour instantanément (et pas après
       le re-fetch Shopify ~1s) quand le client change une quantité avec un
       code de palier appliqué. On lit la table des paliers (mêmes données que
       le popup/​barre) et on recalcule la remise côté client. */
    let _tiers = [];
    try {
      const _tEl = document.getElementById('onix-promo-suggest') || document.querySelector('[data-tiers]');
      if (_tEl && _tEl.dataset.tiers) _tiers = JSON.parse(_tEl.dataset.tiers).sort((a, b) => a.qty - b.qty);
    } catch (_) {}
    let _appliedTier = null;   // palier dont le code est ACTIF dans le panier (ou null)
    let _cartHasBundle = false; // un bundle change la logique → on délègue au serveur
    /* ⚠️ Vrai quand le panier ne contient QUE le pack 2 vestes — 29/08.
       L'en-tête du tiroir annonce un prix moyen à l'unité (« @@ le maillot »).
       Sur un pack, ce chiffre est deux fois faux : il dit « maillot » pour des
       vestes, et il affiche 51,00 € alors que la carte montre exprès 59,99 €
       et 42,00 €. Trois prix pour deux articles. */
    let _cartItemsPourLibelle = [];
    let _cartPackSeul = false;
    /* Montant de remise (cents) d'un palier sur un sous-total donné.
       label "-20%" → pourcentage du sous-total ; "-30€"/"-44,98€" → € fixe. */
    /* ⚠️ `tier.pct` PASSE AVANT L'ÉTIQUETTE — 03/09. Les codes onixtoast /
       onixgift / onixfree sont passés en POURCENTAGE côté Shopify (25 / 28 / 31 %)
       alors que le client lit toujours « -25€ », « -35€ », « -70€ » : ces
       montants sont l'équivalent ARRONDI sur un panier de maillots à 24,99 €,
       pas la formule. Lire l'étiquette revenait à retirer 25,00 € fixes là où
       Shopify en retire 25 % — exact au centime sur des maillots, faux de
       54,99 € sur quatre vestes à 79,99 €.
       ⚠️ LE REPLI SUR L'ÉTIQUETTE RESTE, il n'est pas mort : un palier ajouté
         dans l'éditeur de thème sans `pct` continue de fonctionner comme avant.
       ⚠️ `pct` s'accepte en 25 comme en 0.25 — `second-jersey.js` le lit déjà
         avec cette même tolérance, on ne crée pas une seconde convention. */
    const tierDiscountCents = (tier, itemsSubtotalCents) => {
      if (!tier) return 0;
      if (tier.pct != null && tier.pct !== '') {
        const n = Number(tier.pct);
        if (Number.isFinite(n) && n > 0) return Math.floor(itemsSubtotalCents * (n > 1 ? n / 100 : n));
      }
      if (!tier.label) return 0;
      const num = parseFloat(String(tier.label).replace(/[^0-9,.]/g, '').replace(',', '.')) || 0;
      /* floor, pas round : Shopify TRONQUE les remises en % (15% de 49,98€ =
         7,49€ et non 7,50€). Un round afficherait 1 centime de plus que le
         total réellement facturé. */
      if (/%/.test(tier.label)) return Math.floor(itemsSubtotalCents * Math.abs(num) / 100);
      return Math.round(Math.abs(num) * 100);
    };

    const animateCount = (el, targetCents, duration, key) => {
      key = key || 'subtotal';
      const startCents = __lastValues[key] || 0;
      __lastValues[key] = targetCents;
      /* Set target synchronously so the node is never blank if rAF is
         throttled (hidden drawer / background tab). */
      el.textContent = formatMoney(targetCents);
      if (startCents === targetCents) return;
      const startTime = performance.now();
      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(startCents + (targetCents - startCents) * eased);
        el.textContent = formatMoney(current);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    /* Anime le 2e total « Klarna » (mensualité = total / N) avec EXACTEMENT le
       même compteur/easing que le sous-total, partout où le sous-total change.
       Masque la ligne si le panier est vide. Clé 'klarna' → suit sa valeur
       précédente dans __lastValues comme le fait 'subtotal'. */
    const updateKlarnaAmount = (totalCents, duration) => {
      const klarnaLine = document.querySelector('[data-klarna-line]');
      if (!klarnaLine) return;
      const kn = parseInt(klarnaLine.getAttribute('data-klarna-n'), 10) || 4;
      const klarnaAmtEl = klarnaLine.querySelector('[data-klarna-amount]');
      if (klarnaAmtEl) animateCount(klarnaAmtEl, Math.round(totalCents / kn), duration, 'klarna');
      klarnaLine.hidden = totalCents <= 0;
    };

    /* « Aller à la page de paiement » ne doit pas être cliquable sur un panier
       vide — signalé le 15/08 : le bouton restait actif à 0 article, et menait à
       un /checkout que Shopify renvoie aussitôt.
       ⚠️ ON COMPTE LES ARTICLES, PAS LE TOTAL. Un panier à 0,00 € avec des
         articles (remise à 100 %) doit rester payable ; un panier vide ne l'est
         jamais. Le total ne distingue pas les deux.
       ⚠️ `pointer-events: none` seul ne suffit pas : au clavier, Entrée suit le
         lien quand même. D'où le garde dans le gestionnaire de clic aussi. */
    const majBoutonPaiement = (nbArticles) => {
      const bouton = document.querySelector('.onix-cart-drawer__checkout');
      if (!bouton) return;
      const n = Number.isFinite(nbArticles)
        ? nbArticles
        : parseInt((cartCountNodes[0] && cartCountNodes[0].textContent) || '0', 10) || 0;
      if (n > 0) bouton.removeAttribute('aria-disabled');
      else bouton.setAttribute('aria-disabled', 'true');
      /* Le montant disparaît avec le panier vide — sinon le bouton grisé
         afficherait « 0,00 € », ce qui se lit comme un prix et non comme un
         panier vide. Même compteur d'articles que l'état désactivé : les deux
         ne doivent jamais diverger. */
      const total = bouton.querySelector('[data-checkout-total]');
      if (total) total.hidden = n <= 0;
      /* Le prix barré suit le total : un panier vide ne barre rien. Il peut en
         revanche rester masqué avec un panier plein — c'est majTotalBouton qui
         décide alors, selon qu'il y a un écart à montrer ou non. */
      const compare = bouton.querySelector('[data-checkout-compare]');
      if (compare && n <= 0) compare.hidden = true;
    };

    /* ═══ « JE COMMANDE » — LE SEUL CLIC DU TIROIR SANS AUCUN RETOUR ═══════
       Mesuré le 23/08 au banc, échantillon à chaque frame (~16 ms) : ZÉRO
       changement visible pendant les 2 500 ms du plafond de test, quand tous
       les autres contrôles du tiroir répondent entre 10 et 16 ms. Le lien part
       vers /checkout et l'écran reste identique tant que Shopify n'a pas rendu
       sa page — une à trois secondes sur mobile. C'est le clic qui vaut le plus
       cher de tout le tiroir, et c'était le seul muet.
       Rien n'empêchait non plus un DEUXIÈME appui pendant ce temps mort.
       ⚠️ ET IL LUI FAUT SON CHEMIN DE RETRAIT. Un client qui revient par le
         bouton « précédent » retrouve la page par le bfcache, DOM intact : sans
         retrait il retrouverait un bouton MORT. C'est exactement la régression
         du 23/08 — un état posé sans son inverse. D'où les trois sorties :
         le retour de page (`pageshow`), le retour d'onglet
         (`visibilitychange`), et un filet en dur si aucun des deux n'arrive. */
    const FILET_PAIEMENT_MS = 8000;
    let _filetPaiement = null;
    const retirerPaiementEnCours = () => {
      if (_filetPaiement) { clearTimeout(_filetPaiement); _filetPaiement = null; }
      document
        .querySelectorAll('.onix-cart-drawer__checkout[aria-busy="true"]')
        .forEach((b) => b.removeAttribute('aria-busy'));
    };
    const poserPaiementEnCours = (bouton) => {
      bouton.setAttribute('aria-busy', 'true');
      if (_filetPaiement) clearTimeout(_filetPaiement);
      _filetPaiement = setTimeout(retirerPaiementEnCours, FILET_PAIEMENT_MS);
    };

    /* Le total du bouton suit EXACTEMENT le sous-total : même valeur, même
       animation, même instant. Clé 'checkout' pour qu'il ait sa propre valeur
       de départ dans __lastValues — sans ça il repartirait de celle du
       sous-total et le compteur sauterait. */
    /* ═══ LE PRIX AU MAILLOT DANS L'EN-TÊTE — 19/08 ═══════════════════
       « une unité de mesure pour le client » : il garde en tête ce que lui
       coûte UN maillot pendant qu'il regarde son panier.
       ⚠️ Le total DIVISÉ par le nombre d'articles NON-BUNDLE. `__lastValues.count`
         exclut déjà les lignes de bundle, qui ont leur propre prix — les
         inclure ferait un prix unitaire qui ne correspond à aucun maillot.
       ⚠️ Masqué sur un panier vide : il n'y a pas d'unité à mesurer. */
    const majUnite = (totalCents) => {
      const el = document.querySelector('[data-unite]');
      if (!el) return;
      const n = __lastValues.count || 0;
      if (n <= 0 || !(totalCents > 0)) { el.hidden = true; return; }
      /* le pack porte ses propres prix, ligne par ligne : une moyenne à côté
         ne renseigne pas, elle contredit. */
      if (_cartPackSeul) { el.hidden = true; return; }
      // ⚠️ Le <span> enveloppant n'est pas décoratif : voir le commentaire du
      //   snippet. Conteneur flex ⇒ deux enfants ⇒ l'espace disparaît.
      // ⚠️ Et le texte vient de la LOCALE, découpé sur le même jeton '@@' que
      //   le snippet : les cinq langues sont publiées, « le maillot » en dur
      //   partait tel quel chez un visiteur allemand.
      /* ⛔ « LE MAILLOT » NE VAUT QUE POUR UN PANIER DE MAILLOTS — 30/08, sa
         remarque. Son écran : « 36,12 € le maillot » sur un panier contenant une
         veste et un maillot. Le prix moyen est juste ; le mot ne l'est pas.
         ⚠️ Détection par le TITRE, comme partout ailleurs dans ce thème :
           `productType` est vide sur tout le catalogue, et la collection
           « vestes » utilise déjà « TITLE CONTAINS Veste » depuis le 22/08. */
      const panierHomogene = !(_cartItemsPourLibelle || []).some(t => /veste/i.test(t))
        || (_cartItemsPourLibelle || []).every(t => /veste/i.test(t));
      const gabarit = panierHomogene
        ? (drawerRoot.dataset.tPerJersey || '@@ le maillot')
        : (drawerRoot.dataset.tPerItem || drawerRoot.dataset.tPerJersey || '@@ l’article');
      const morceaux = gabarit.split('@@');
      el.innerHTML = '<span>' + escapeHtml(morceaux[0] || '')
        + '<b>' + escapeHtml(formatMoney(Math.round(totalCents / n))) + '</b>'
        + escapeHtml(morceaux[1] || '') + '</span>';
      el.hidden = false;
    };

    const majTotalBouton = (totalCents, duration, compareCents) => {
      const el = document.querySelector('.onix-cart-drawer__checkout [data-checkout-total]');
      if (el) animateCount(el, totalCents, duration, 'checkout');

      /* ── LE PRIX AVANT RÉDUCTION, à côté du total — 18/08 ──────────────
         ⚠️ IL DISPARAÎT DÈS QU'IL N'Y A RIEN À BARRER. Sans remise, le prix
           barré vaut le total : afficher « 79,97 € 79,97 € » se lit comme un
           bug, pas comme une absence de remise.
         ⚠️ AU MOMENT DE RÉAPPARAÎTRE, LA VALEUR SE POSE, ELLE NE S'ANIME PAS.
           Le compteur repart de sa valeur précédente ; après un passage à
           l'état masqué celle-ci n'a plus de sens, et le prix barré remonterait
           depuis un montant que le client n'a jamais vu. On réamorce donc
           __lastValues avant d'animer. */
      const cmp = document.querySelector('.onix-cart-drawer__checkout [data-checkout-compare]');
      if (!cmp) return;
      const aBarrer = Number.isFinite(compareCents) && compareCents > totalCents;
      if (!aBarrer) { cmp.hidden = true; return; }
      if (cmp.hidden) { __lastValues.checkoutCompare = compareCents; cmp.hidden = false; }
      animateCount(cmp, compareCents, duration, 'checkoutCompare');
    };

    const animatePrice = (el, fromCents, toCents, duration) => {
      if (fromCents === toCents) { el.textContent = formatMoney(toCents); return; }
      /* Sync fallback in case rAF is throttled. */
      el.textContent = formatMoney(toCents);
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = formatMoney(Math.round(fromCents + (toCents - fromCents) * eased));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const clearPendingOpen = () => {
      try {
        window.sessionStorage.removeItem("onixOpenCart");
      } catch (error) {}
    };

    const setPendingOpen = () => {
      try {
        window.sessionStorage.setItem("onixOpenCart", "1");
      } catch (error) {}
    };

    /* ⛔ `?openCart=1` NE FAISAIT QUE SE NETTOYER LUI-MÊME — 05/09. Ce bloc lisait
       le paramètre, le retirait de la barre d'adresse… et personne n'en faisait
       rien : le tiroir ne s'ouvrait pas. L'ouverture ne venait QUE de
       `sessionStorage.onixOpenCart`, posé par un clic sur un lien `/cart`.
       Un paramètre d'URL qu'aucun fichier du thème n'émet et qui n'a aucun effet
       est du code mort qui a l'air d'un contrat.
       ➜ On le rend VRAI : il ouvre le tiroir, comme son nom le dit. C'est aussi le
       seul moyen de MONTRER le tiroir par un simple lien (scripts/montrer.js
       n'ouvre pas de menu, il lit une page).
       ⚠️ ON LIT AVANT DE NETTOYER. `delete` puis `get` renverrait toujours null. */
    let ouvrirDepuisUrl = false;
    const cleanupOpenCartParam = () => {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get("openCart") !== "1") return;
      ouvrirDepuisUrl = true;

      currentUrl.searchParams.delete("openCart");
      window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    };

    cleanupOpenCartParam();

    /* ── a11y focus management ──────────────────────────────────
       Remember which element had focus before the drawer opened,
       so we can restore it on close. Focus trap keeps keyboard Tab
       navigation confined to the panel while open. */
    let _drawerLastFocused = null;

    const getDrawerFocusables = () => {
      return drawerPanel.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    };

    const onDrawerTabTrap = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = getDrawerFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const openDrawer = () => {
      /* Un tiroir qu'on rouvre ne montre JAMAIS un bouton de paiement resté
         « en cours » d'un passage précédent — c'est la 1re des trois sorties
         de l'état posé par poserPaiementEnCours. */
      retirerPaiementEnCours();
      _drawerLastFocused = document.activeElement;
      drawerRoot.removeAttribute("hidden");
      drawerRoot.classList.add("is-open");
      drawerPanel.setAttribute("aria-hidden", "false");
      cartToggleButtons.forEach((button) => button.setAttribute("aria-expanded", "true"));
      document.body.style.overflow = "hidden";
      drawerPanel.addEventListener('keydown', onDrawerTabTrap);
      /* Move focus to the first focusable inside the drawer AFTER the
         open transition so the element is actually on-screen. Using
         setTimeout (not rAF) because the panel slides in over ~300ms
         and early focus() calls are ignored by some browsers when the
         target is still off-screen via transform: translateX(100%). */
      setTimeout(() => {
        if (!drawerRoot.classList.contains("is-open")) return;
        const firstFocusable = getDrawerFocusables()[0];
        if (firstFocusable) firstFocusable.focus();
      }, 350);
    };

    /* Drawer close animation duration, must stay in sync with the
       CSS transition on .onix-cart-drawer.is-open. If you change this
       value, also update assets/cart-drawer.css. */
    const CLOSE_ANIMATION_MS = 400;

    const closeDrawer = () => {
      drawerRoot.classList.remove("is-open");
      drawerPanel.setAttribute("aria-hidden", "true");
      cartToggleButtons.forEach((button) => button.setAttribute("aria-expanded", "false"));
      document.body.style.overflow = "";
      drawerPanel.removeEventListener('keydown', onDrawerTabTrap);
      /* Return focus to the element that opened the drawer (cart icon,
         quick-add button, etc.) so keyboard users don't lose their place. */
      if (_drawerLastFocused && typeof _drawerLastFocused.focus === 'function') {
        try { _drawerLastFocused.focus(); } catch (e) { /* element gone */ }
      }
      _drawerLastFocused = null;
      /* Keep DOM hidden for a11y after the close animation finishes. */
      setTimeout(() => {
        if (!drawerRoot.classList.contains("is-open")) drawerRoot.setAttribute("hidden", "");
      }, CLOSE_ANIMATION_MS);
    };

    const itemSizeValue = (item) => item.variant_title || "";

    const itemImage = (item) => {
      if (item.featured_image && item.featured_image.url) return item.featured_image.url;
      if (item.image) return item.image;
      return "";
    };

    /* Bundle detection: true if any line has _offer_bundle property.
       All pricing comes from Shopify (cart.total_price / item.final_line_price)
       so the drawer always matches what checkout will charge. */
    const hasBundle = (cart) => cart.items.some(i => i.properties && i.properties._offer_bundle === 'true');

    /* Fallback surcharge per extra bundle unit when a bundle line's
       data-full-unit attribute is missing. The authoritative value is read
       dynamically from `priceEl.dataset.fullUnit` on every click — this
       constant is only used as a last-resort safety net for optimistic UI,
       and the server is always the source of truth on next render. */
    const BUNDLE_EXTRA_FALLBACK_CENTS = 2499;

    /* ── Shared bundle-card markup helpers ──
       Single source of truth used by BOTH renderCart() (server-authoritative)
       and __onixInjectOptimisticBundle() (client optimistic). Prevents the
       two paths from drifting (the "15€/15€" bug was caused by divergent
       markup between these two sources). */

    /**
     * Build one `<article>` row inside the bundle card.
     * @param {object} o - Row config.
     * @param {string} o.displayKey - value for data-cart-item-key.
     * @param {string|number} o.variantId - variant id for data-variant-id.
     * @param {string} o.url - product URL.
     * @param {string} o.title - visible title.
     * @param {string} o.mediaHtml - prebuilt `<a>` or `<div>` for the image.
     * @param {string} o.removeBtnHtml - prebuilt remove button markup.
     * @param {string} o.sizePillHtml - prebuilt size pill markup (or '').
     * @param {number} o.quantity - qty to display in the locked stepper.
     * @param {string} o.comparePriceHtml - prebuilt compare span (or '').
     * @param {string} o.priceAttrs - extra data-* attrs on the price span.
     * @param {number} o.priceCents - cents to display in the price span.
     * @param {string} [o.extraArticleAttrs] - extra attrs on the article tag.
     * @returns {string} HTML string.
     */
    const buildBundleRowHtml = (o) => `
      <article class="onix-bundle-card__item" data-cart-item-key="${escapeHtml(o.displayKey)}" data-variant-id="${escapeHtml(String(o.variantId))}" data-bundle="1"${o.extraArticleAttrs || ''}>
        ${o.mediaHtml}
        <div class="onix-bundle-card__body">
          <div class="onix-bundle-card__head">
            <h3 class="onix-bundle-card__title"><a href="${escapeHtml(o.url || '#')}">${escapeHtml(o.title || '')}</a></h3>
            ${o.removeBtnHtml}
          </div>
          <div class="onix-bundle-card__meta">
            ${o.sizePillHtml || ''}
            <span class="onix-bundle-card__ready">${escapeHtml(t.inStock)}</span>
          </div>
          <div class="onix-bundle-card__foot">
            <div class="onix-bundle-card__qty is-locked" aria-label="${escapeHtml(t.quantity)}">
              <button type="button" disabled aria-disabled="true">−</button>
              <span>${o.quantity}</span>
              <button type="button" disabled aria-disabled="true">+</button>
            </div>
            <div class="onix-bundle-card__pricing">
              ${o.comparePriceHtml || ''}
              <span class="onix-bundle-card__price"${o.priceAttrs || ' data-bundle="1"'}>${formatMoney(o.priceCents)}</span>
            </div>
          </div>
        </div>
      </article>`;

    /**
     * Wrap a list of pre-rendered rows in the bundle `<section>` shell.
     * @param {string} rowsHtml - concatenated row HTML.
     * @param {number} totalCents - bundle total shown in the header.
     * @returns {string} HTML string.
     */
    const buildBundleSectionHtml = (rowsHtml, totalCents, label, extraClass) => `
      <section class="onix-bundle-card${extraClass ? ' ' + extraClass : ''}">
        <header class="onix-bundle-card__header">
          <span class="onix-bundle-card__badge">${escapeHtml((label || t.bundleLabel || 'Offre Découverte').toUpperCase())}</span>
          <span class="onix-bundle-card__total">${formatMoney(totalCents || 0)}</span>
        </header>
        <div class="onix-bundle-card__items">${rowsHtml}</div>
      </section>`;

    /* Reused remove-button SVG (inlined once to avoid markup duplication). */
    const BUNDLE_REMOVE_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>';

    /* loggedFetch is now defined at the IIFE top-level (see line ~40) so
       initQuickAdd and initDiscount can reach it too. */

    /**
     * Server-authoritative cart render. Wipes `drawerBody`, rebuilds the
     * bundle card (if any) plus every regular line, and reconciles the
     * footer (subtotal, compare-at, discount details). Suppressed while
     * optimistic writes are still pending via `hasPendingWrites()` so the
     * UI never flickers back to stale server state mid-edit.
     * @param {object} cart - Shopify cart.js payload.
     */
    const renderCart = (cart) => {
      cartCountNodes.forEach((node) => {
        node.textContent = cart.item_count;
      });
      majBoutonPaiement(cart.item_count);

      /* Notifie tous les modules externes (bar promo, popup tier-suggest)
         qu'un nouvel état cart vient d'être rendu. On dispatch en tick
         suivant pour que renderCart finisse d'abord (badges, drawer DOM),
         et on lève un flag interne pour que le listener cart:refresh de
         CE drawer ne re-déclenche pas son propre safeRefreshCart (boucle). */
      requestAnimationFrame(function () {
        /* On embarque le cart fraîchement rendu dans l'event : les modules
           externes (jauge + popup tier) peuvent l'afficher INSTANTANÉMENT
           sans re-fetch /cart.js ni attendre leur debounce. */
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
      });

      const capMap = window.__onixCompareAtPrices || {};
      const bundleActive = hasBundle(cart);

      if (!bundleActive && window.__onixOfferDiscountCode) {
        delete window.__onixOfferDiscountCode;
      }

      /* Discount UI stays visible even when a bundle is active — the
         user can see the input and try to apply a code, but
         cart-drawer-discount.js will show "Non cumulable avec l'offre"
         if bundleIsActive() is true. Previously we hid the whole block
         which confused users who didn't understand why the field vanished. */

      /* ⛔ UN BLOC VIDE COÛTE DEUX ESPACEMENTS — 30/08, 09h15. Sa remarque sur
           le vide entre les paliers et la livraison : 42 px mesurés, dont 27
           venaient de ce seul bloc, à hauteur ZÉRO.
         Il n'a du contenu que pour les codes qui ne sont pas des paliers
         (collab, roue) : le JS y accroche alors une pastille de confirmation.
         Pour un palier, la confirmation est sur la marche allumée et ce bloc
         reste vide — mais il occupait quand même sa cellule dans la grille.
         ⚠️ ON LE MASQUE DYNAMIQUEMENT, JAMAIS EN DUR : un `display: none` sur ce
           conteneur a déjà été posé le 17/08 et a fait disparaître la pastille.
           Ici on regarde ce qu'il CONTIENT au moment du rendu. */
      if (subtotalNode) {
        /* ⛔ LE CRITÈRE EST CE QUE LE BLOC CONTIENT, PAS SA HAUTEUR — corrigé
             dans le tour même, 30/08 09h25, APRÈS avoir refait la faute du 17/08.
             Ma première version mesurait la hauteur des enfants dans un
             requestAnimationFrame. La pastille de remise est ajoutée APRÈS ce
             moment-là : je la masquais donc alors qu'elle venait d'arriver.
             Vérifié : code EVILDINO appliqué, le bloc contenait « ✓ Promo -8,99€ »
             ET portait `hidden`. Le client voit son prix baisser sans qu'aucune
             ligne ne dise pourquoi — mot pour mot le défaut du 17/08, que le
             commentaire d'à côté décrit et que je venais de reproduire.
           ➜ On ne mesure plus une géométrie à un instant : on regarde la
             PRÉSENCE de la ligne de remise, qui est la seule chose que ce bloc
             ait jamais à montrer. Un fait booléen, pas un pixel dans le temps.
           ⚠️ Les deux montants du sous-total sont masqués par CSS depuis le
             18/08 (le total est déjà sur le bouton) : ils ne comptent pas. */
        /* ⚠️ ET APRÈS LE RENDU, PAS PENDANT. Ce bloc de code s'exécute vers la
             ligne 650 ; la pastille de remise est ajoutée vers la ligne 802,
             dans la même fonction. Tester ici revient à demander si un objet
             existe avant de l'avoir créé — j'ai eu `a_la_pastille: true` ET
             `masqué: true` dans la même mesure avant de comprendre.
           Le requestAnimationFrame attend la fin du tour de boucle : à ce
           moment la pastille est posée si elle devait l'être.
           ⚠️ Deux erreurs successives ici, et elles sont l'inverse l'une de
             l'autre : d'abord le bon MOMENT (rAF) avec le mauvais CRITÈRE (la
             hauteur), puis le bon critère au mauvais moment. Il fallait les
             deux. */
        const bloc = subtotalNode.closest('.onix-cart-drawer__subtotal') || subtotalNode;
        requestAnimationFrame(() => {
          bloc.toggleAttribute('hidden', !bloc.querySelector('.onix-cart-drawer__remise'));
        });
      }
      if (subtotalNode) {
        /* Trust Shopify: cart.total_price already reflects any applied
           discount (bundle ONIIX or user code). This keeps the drawer
           in sync with the actual checkout total. */
        const adjustedTotal = cart.total_price;

        /* État pour le recalcul optimiste de la remise (cf. optimisticSubtotal) :
           palier ACTIF (remise réellement appliquée par Shopify), présence d'un
           bundle, sous-total HORS remise et compteur non-bundle. */
        _cartHasBundle = (cart.items || []).some(i => i.properties && i.properties._offer_bundle === 'true');
        _cartPackSeul = (cart.items || []).length > 1
          && (cart.items || []).every(i => i.properties && i.properties._pack_veste);
        const _activeTitles = [];
        (cart.cart_level_discount_applications || []).forEach(a => { if (a.title && a.total_allocated_amount > 0) _activeTitles.push(a.title.toUpperCase()); });
        (cart.items || []).forEach(i => (i.discounts || []).forEach(d => { if (d.title) _activeTitles.push(d.title.toUpperCase()); }));
        _appliedTier = (!_cartHasBundle && _tiers.length) ? (_tiers.find(t => _activeTitles.includes(t.code.toUpperCase())) || null) : null;
        __lastValues.itemsSubtotal = cart.items_subtotal_price;
        __lastValues.count = (cart.items || []).reduce((s, i) => s + ((i.properties && i.properties._offer_bundle === 'true') ? 0 : (i.quantity || 0)), 0);

        let compareTotal = 0;
        cart.items.forEach(item => {
          const cap = capMap[item.variant_id];
          const unitCompare = cap && cap > item.final_price
            ? cap
            : (item.original_price && item.original_price > item.final_price ? item.original_price : item.final_price);
          compareTotal += unitCompare * item.quantity;
        });
        /* Compare baseline must be at least the pre-discount items total so
           the strike-through is never lower than the live subtotal. */
        if (compareTotal < cart.items_subtotal_price) compareTotal = cart.items_subtotal_price;
        const hasCompare = compareTotal > adjustedTotal;

        /* Reuse existing sub-nodes instead of re-injecting innerHTML so
           the compare span stays in place across renders (liquid
           pre-renders it; we just update textContent). This keeps the
           layout stable whether the cart is empty, has items without
           compare, or items with compare. */
        let amountEl = subtotalNode.querySelector('[data-subtotal-amount]');
        let compareEl = subtotalNode.querySelector('[data-compare-amount]');
        if (!amountEl) {
          /* Filet de secours si le bloc a été vidé. ⚠️ Il DOIT recréer le
             conteneur `.onix-cart-drawer__subtotal-values` : c'est lui qui porte
             le `display: none`. Sans lui, les montants réapparaîtraient à
             l'écran alors qu'ils sont dans le bouton depuis le 17/08. */
          subtotalNode.insertAdjacentHTML('afterbegin',
            '<strong data-cart-subtotal class="onix-cart-drawer__subtotal-values">'
            + '<span data-subtotal-amount></span>'
            + '<span data-compare-amount class="onix-cart-drawer__subtotal-compare"></span>'
            + '</strong>');
          amountEl = subtotalNode.querySelector('[data-subtotal-amount]');
          compareEl = subtotalNode.querySelector('[data-compare-amount]');
        }
        /* Compare span stays permanently visible. Its value tracks
           compareTotal (fallback to adjustedTotal when there's no real
           saving, so it still displays a sensible number rather than 0
           when the cart is either empty or has no compare prices). */
        const compareValue = hasCompare ? compareTotal : adjustedTotal;
        animateCount(amountEl, adjustedTotal, 500, 'subtotal');
        if (compareEl) animateCount(compareEl, compareValue, 500, 'compare');
        /* Même montant, même animation, au même instant que le sous-total —
           les deux affichent `adjustedTotal`, donc ils ne peuvent pas diverger. */
        majTotalBouton(adjustedTotal, 500, hasCompare ? compareTotal : 0);
        majUnite(adjustedTotal);

        /* 2e total « Klarna » : mensualité = total / N (4), animée comme le
           sous-total à chaque changement de panier. Masquée si panier vide. */
        updateKlarnaAmount(adjustedTotal, 500);

        /* Remove any previously-rendered cart-level discount rows, then
           re-inject the fresh set (so rows are always in sync).

           ⚠️ `/cart/change.js` NE RENVOIE PAS LES REMISES, ET NE LE DIT PAS.
             Mesuré le 18/08 sur un panier remisé par `onix4` :
               réponse de /cart/change.js → cart_level_discount_applications: [{}]
               /cart.js dans la seconde   → [{ title:'onix4', total_allocated_amount:1999 }]
             Le tableau n'est pas vide — il contient UN OBJET VIDE. La longueur
             vaut donc 1, on entre dans le bloc, le filtre ne garde rien, et les
             lignes qu'on venait de supprimer ne sont jamais remises. Résultat :
             la ligne « 🏷 Promo : -14,99 € » DISPARAÎT au moindre changement de
             quantité alors que la remise s'applique toujours — le total reste
             juste, seule l'explication du rabais s'efface. Le client voit son
             panier baisser sans savoir pourquoi.
             ⚠️ Défaut ANTÉRIEUR, vérifié le 18/08 sur le thème live #198383796607
               avant toute modification : même disparition, aux mêmes instants.
           ➜ On ne détruit les lignes que si la charge utile porte VRAIMENT des
             remises exploitables. Sinon on laisse en place ce que le chemin
             optimiste a déjà posé — il a le bon montant, mesuré à +400 ms. */
        const remisesUtilisables = (cart.cart_level_discount_applications || [])
          .filter(d => d.title && d.total_allocated_amount > 0);
        const chargeMuette = (cart.cart_level_discount_applications || []).length > 0
          && remisesUtilisables.length === 0;
        if (!chargeMuette) {
          subtotalNode.querySelectorAll('[data-cart-discount-row]').forEach(n => n.remove());
        }
        if (!chargeMuette && remisesUtilisables.length) {
          /* ⚠️ D4 — ON NE REND PAS DE LIGNE POUR UN PALIER. Sa confirmation
             est sur la marche allumée de l'échelle : coche + « appliqué ».
             En rendre une ici ferait revenir le corps étranger, et dirait deux
             fois la même chose à vingt pixels d'écart.
             Les codes qui NE SONT PAS des paliers — collab, roue — n'ont pas de
             marche à cocher : pour eux la ligne reste, et c'est le seul endroit
             où le client voit que son code a pris. */
          const discounts = remisesUtilisables.filter(d => !libellePalier(d.title));
          discounts.forEach(d => {
            const row = document.createElement('span');
            row.setAttribute('data-cart-discount-row', '');
            /* ⚠️ PLUS DE STYLE EN LIGNE. L'ancienne version posait la couleur et
               la taille en dur ici, donc le design de cette ligne n'était nulle
               part dans la feuille et ne pouvait pas se retoucher sans passer
               par le JS. Tout est dans `.onix-cart-drawer__remise`. */
            row.className = 'onix-cart-drawer__remise';
            /* ⚠️ TOUJOURS PAS LE CODE (règle du 02/08) : le code n'apprend rien
               au client à ce stade et invite au partage ou à la recherche d'un
               « meilleur » code ailleurs.
               Mais « Promo » tout court ne dit rien non plus — 18/08, « le texte
               est trop générique ». On nomme donc CE QUI A DÉCLENCHÉ la remise :
               « Remise 3 maillots ». Le client relie ce qu'il a fait à ce qu'il
               gagne, et ça ne révèle aucun code.
               Repli sur le libellé générique pour un code qui n'est pas un
               palier — un code de collab, la roue — dont on ne sait rien dire. */
            /* ⚠️ LE MONTANT ÉCONOMISÉ N'EST PLUS ICI — il est DÉJÀ sur la marche
               atteinte de l'échelle, dix pixels plus haut. Le répéter en faisait
               la seule information affichée deux fois dans le tiroir.
               À la place, la seule chose que rien n'affiche : LE PRIX AU MAILLOT.
               C'est l'argument de la boutique — « plus tu en prends, moins c'est
               cher » — et il n'a pas sa place sur chaque carte produit, où il
               serait répété autant de fois qu'il y a de lignes. Un seul endroit,
               ici, au niveau du total. */
            /* Le prix au maillot est monté dans l'en-tête ; ici il ne reste
               que le fait, et le montant que le code a fait gagner. */
            row.innerHTML = '<i>✓</i>' + escapeHtml(t.promoLabel || 'Promo')
              + ' <b>-' + sansCentimesNuls(formatMoney(d.total_allocated_amount)) + '</b>';
            subtotalNode.appendChild(row);
          });
        }
      }

      if (!drawerBody) return;

      if (!cart.items.length) {
        drawerBody.innerHTML = `
        <div class="onix-cart-drawer__empty"></div>
      `;
        /* #1 best-seller suggestions inject into the empty state (cart-drawer-suggest.js). */
        if (window.__onixRenderEmptyExtras) window.__onixRenderEmptyExtras(drawerBody);
        return;
      }

      /* Bundle lines with the same variant can be split by Shopify when the
         ONIIX discount allocates different amounts per unit. We merge them
         back into a single visual card per variant so +/- increments the
         same card rather than creating a new one.

         Visual re-allocation: l'offre découverte (oniix/onm) est une remise
         -40% répartie uniformément par Shopify sur chaque ligne bundle (~15€
         par maillot). On étale le vrai total bundle uniformément sur toutes
         les unités (reste de centimes sur la 1re ligne) → la somme des lignes
         = total bundle (29,99€ / 44,99€), et cart.total_price reste le
         sous-total qui fait foi (= checkout). */
      let cartReallocation = null;
      if (bundleActive) {
        /* 1re ligne bundle = porteuse du reste de centimes de l'étalement. */
        const bundleItemsList = cart.items.filter(i => i.properties && i.properties._offer_bundle === 'true');
        const firstBundleItem = bundleItemsList[bundleItemsList.length - 1];
        if (firstBundleItem) {
          /* Compute the bundle-specific saving from line-level allocations
             on bundle items ONLY. This avoids two traps the old
             (fullTotal − cart.total_price) formula had:
               - it counted tier discounts (from the Function) that apply
                 to non-bundle items → over-estimated the bundle saving
                 and pushed the visual bundle unit to 0 €,
               - it depended on an exact "oniix" title match, fragile if
                 the merchant ever renames the discount in admin.

             All discounts allocated to bundle line items are, by design,
             bundle-specific (the Function excludes bundle lines from
             tier discounts — see extensions/onix-bundle-aware-discount
             /src/run.ts). So we can sum every allocation on bundle
             items without filtering by title. */
          const bundleItemsAll = cart.items.filter(i => i.properties && i.properties._offer_bundle === 'true');
          const oniixSavingCents = bundleItemsAll.reduce((sum, i) => {
            const allocs = i.line_level_discount_allocations || [];
            return sum + allocs.reduce((s, a) => s + (a.amount || 0), 0);
          }, 0);
          const bundleQtyTotal = bundleItemsAll.reduce((s, i) => s + i.quantity, 0);
          const bundleFullTotal = bundleItemsAll.reduce((s, i) => s + i.price * i.quantity, 0);
          const bundleTargetTotal = Math.max(0, bundleFullTotal - oniixSavingCents);
          if (oniixSavingCents > 0 && bundleQtyTotal > 0) {
            /* Offre découverte = remise -40% (oniix/onm depuis 2026-06-19).
               Shopify répartit le -40% UNIFORMÉMENT sur chaque ligne (≈15€ par
               maillot, stable — plus de BXGY qui faisait sauter la ligne
               discountée). On étale donc le vrai total bundle uniformément sur
               toutes les unités, le reste de centimes posé sur la 1re ligne →
               somme des lignes = total bundle (29,99€ / 44,99€). */
            const perUnit = Math.floor(bundleTargetTotal / bundleQtyTotal);
            cartReallocation = {
              mode: 'flat',
              holderVariantId: firstBundleItem.variant_id,
              perUnit,
              remainder: bundleTargetTotal - perUnit * bundleQtyTotal,
            };
          }
        }
      }

      /* les titres servent au libellé « X € le maillot / l'article » plus haut */
      _cartItemsPourLibelle = (cart.items || []).map(i => i.title || '');
      const renderItems = [];
      const bundleGroups = new Map();
      cart.items.forEach((item) => {
        const isBundle = bundleActive && item.properties && item.properties._offer_bundle === 'true';
        if (!isBundle) {
          renderItems.push(item);
          return;
        }
        const existing = bundleGroups.get(item.variant_id);
        if (existing) {
          existing.quantity += item.quantity;
          existing.final_line_price += item.final_line_price;
          existing.original_line_price += item.original_line_price;
          existing._bundleKeys.push({ key: item.key, qty: item.quantity });
        } else {
          const merged = Object.assign({}, item, {
            _bundleMerged: true,
            _bundleKeys: [{ key: item.key, qty: item.quantity }],
          });
          bundleGroups.set(item.variant_id, merged);
          renderItems.push(merged);
        }
      });

      /* Split renderItems: bundle items are grouped into a dedicated card
         ("OFFRE DÉCOUVERTE") with locked quantity, per design. Regular items
         render as-is below. */
      const bundleRenderItems = renderItems.filter(it => !!it._bundleMerged || (bundleActive && it.properties && it.properties._offer_bundle === 'true'));

      /* ⚠️ LE PACK 2 VESTES A SA PROPRE CARTE — 29/08, sa demande.
         Sans regroupement, le tiroir montrait « 50,99 € » et « 51,00 € » : la
         remise de 15 % que Shopify répartit ligne par ligne, centime de reste
         compris. Deux prix qui ne correspondent à RIEN de ce que la fiche
         annonce (59,99 € + 42,00 €), et pas un mot pour dire que c'est un pack.
         ⚠️ La clé est `_pack_veste`, JAMAIS `_offer_bundle` : celle-ci
           déclenche l'offre découverte (quantité verrouillée, non-cumul), et
           les deux offres ne se relient pas. On ne partage que le DESSIN. */
      const estPack = (it) => !!(it.properties && it.properties._pack_veste)
        && !(!!it._bundleMerged || (bundleActive && it.properties._offer_bundle === 'true'));
      /* ⚠️ UN PACK, C'EST DEUX LIGNES — 29/08, sa remarque : « quand je supprime
         un maillot du pack l'affiche est toujours là ». La carte restait, badge
         « PACK 2 VESTES » et tout, avec UNE seule veste à 59,99 € : un pack de
         un, à plein tarif, qui promet une économie qui n'existe plus.
         En dessous de deux lignes, ce n'est plus un pack : les articles
         retournent simplement dans la liste normale. Rien n'est supprimé du
         panier — c'est un choix d'AFFICHAGE, le client garde sa veste. */
      /* ⛔ UNE CARTE PAR PACK — 29/08, 18h. Toutes les lignes marquées étaient
         regroupées dans UNE carte « PACK 2 VESTES », quel qu'en soit le nombre :
         deux packs ajoutés à la suite donnaient une carte de quatre vestes.
         Chaque ajout porte désormais son propre identifiant (`duo-<base36>`),
         posé côté fiche produit ; on groupe par cet identifiant. */
      const parPack = new Map();
      for (const it of renderItems.filter(estPack)) {
        const ref = it.properties._pack_veste;
        if (!parPack.has(ref)) parPack.set(ref, []);
        parPack.get(ref).push(it);
      }
      /* sous deux articles ce n'est plus un pack : les vestes retournent dans
         la liste normale plutôt que de porter un badge qui ment. */
      for (const [ref, lignes] of [...parPack]) {
        if (lignes.reduce((n2, i) => n2 + (i.quantity || 0), 0) < 2) parPack.delete(ref);
      }
      const packBrut = [...parPack.values()].flat();
      const packRenderItems = packBrut;

      /* ⛔ TROIS VESTES AU PANIER, DEUX À L'ÉCRAN — 30/08, 06h30. C'EST LUI QUI L'A VU :
           « si je mets un pack dans le panier puis un 2e, et si je supprime un
           maillot d'un pack, visuellement il affiche qu'un pack mais dans le
           panier ça compte encore 3 maillots et au paiement aussi ».

         REPRODUIT EN LABORATOIRE, pas déduit :
           2 packs ajoutés ........... 4 vestes · 239,96 €
           1 veste retirée du pack B . 3 vestes · 179,97 €
           ce que le tiroir montrait . 1 carte, 2 vestes, 0 ligne normale
         La 3e veste était invisible et payée. Un client voit deux vestes,
         règle trois vestes, et découvre la troisième à la livraison.

         LA CAUSE, EN UN MOT : `packEntier` était un BOOLÉEN GLOBAL —
         `packBrut.length > 0`. Dès qu'il existait UN pack valide quelque part,
         le filtre écartait de la liste normale TOUTES les lignes portant la
         marque `_pack_veste`. Or la veste orpheline du pack dissous porte
         toujours cette marque : elle était donc écartée de la liste normale…
         sans être reprise dans aucune carte, puisque son pack avait été retiré
         de `parPack` quelques lignes plus haut.

         ⚠️ C'EST UNE FAUTE DE PORTÉE, PAS DE LOGIQUE. La règle « sous deux
           articles, ce n'est plus un pack » était juste, et le retrait de
           `parPack` aussi. Ce qui manquait, c'est que le filtre d'exclusion se
           fondait sur l'EXISTENCE d'un pack au lieu de l'APPARTENANCE de chaque
           ligne à un pack. Un booléen global pour décrire un fait individuel.

         ➜ On construit l'ensemble des lignes RÉELLEMENT rendues dans une carte,
           et on n'exclut que celles-là. Une ligne qui n'est dans aucune carte
           retombe dans la liste normale — donc reste visible, toujours.
         ⚠️ On compare par `key` : Shopify la garantit unique par ligne de panier,
           là où `variant_id` est partagé entre deux packs du même coloris. */
      const clesDansUneCarte = new Set(packBrut.map(it => it.key));
      const regularRenderItems = renderItems.filter(it =>
        !(!!it._bundleMerged || (bundleActive && it.properties && it.properties._offer_bundle === 'true'))
        && !clesDansUneCarte.has(it.key));

      /* ⚠️ FILET : aucune ligne du panier ne doit disparaître de l'écran. Si le
         compte affiché s'écarte du compte réel, on le dit dans la console — un
         article invisible se paie quand même, et c'est le pire défaut possible
         dans un panier. */
      const rendues = packBrut.length + regularRenderItems.length;
      if (rendues !== renderItems.length) {
        console.warn('[onix] panier : ' + renderItems.length + ' lignes, ' + rendues +
          ' affichées — ' + (renderItems.length - rendues) + ' invisible(s).');
      }

      /* ═══ LA REMISE EST PORTÉE PAR LES ARTICLES AJOUTÉS — 30/08, 07h40 ═══
         Sa demande : « quand on a 2 produits dans le panier et qu'on applique un
         code promo, visuellement faut faire comme si c'était toujours les
         produits en plus qui sont moins chers ».

         CE QUI SE PASSAIT. Shopify répartit une remise de panier au PRORATA sur
         toutes les lignes. Sur son écran : la veste promise à 47,24 € s'affichait
         à 50,99 €, et le maillot déjà présent tombait de 24,99 à 21,25 €.
         Le total (72,24 €) était juste ; la répartition, elle, contredisait la
         promesse. Le client clique sur « 47,24 € » et ne retrouve ce nombre
         NULLE PART — à la place son premier article a baissé sans explication.

         CE QU'ON FAIT. Le premier article garde son prix plein ; les suivants
         absorbent toute la remise. Le total ne bouge pas d'un centime : on ne
         change que la façon dont il est réparti à l'écran.

         ⚠️ ON NE TOUCHE PAS À CE QUE SHOPIFY FACTURE. C'est un affichage, et
           uniquement un affichage. Le jour où la somme des lignes affichées
           s'écarterait du total facturé, on afficherait un prix faux — exactement
           le défaut qu'on est en train de corriger. D'où le contrôle plus bas,
           et le garde `verifier-prix-panier.js`.

         ⛔ L'ORDRE DE `cart.items` EST L'INVERSE DE CE QU'ON CROIT — vérifié le
           30/08 avant d'écrire ce code, sur trois articles ajoutés un par un :
             ajoutés ...... Espagne, puis Veste noire, puis Veste sauge
             cart.items ... Veste sauge, Veste noire, Espagne
           Shopify met le PLUS RÉCENT EN TÊTE. « Celui qui était déjà là » est
           donc `items[items.length - 1]`, pas `items[0]`.
           Ma première version prenait `slice(1)` : elle aurait protégé le
           dernier article ajouté et remisé celui que le client avait depuis le
           début — l'exact inverse de la demande, sans lever la moindre erreur.

         ⚠️ ON N'Y TOUCHE PAS SI UN AUTRE MÉCANISME RÉPARTIT DÉJÀ (offre
           découverte, `cartReallocation`) : deux répartitions concurrentes sur
           les mêmes lignes donneraient une somme fausse. */
      /* ⛔ ON REPART DES PRIX PLEINS, PAS DES PRIX DÉJÀ RÉDUITS — 30/08, 08h.
           Ma première version cherchait « la remise de panier non encore
           répartie » : (plein − total) − (somme des remises par ligne). Mesuré
           sur son cas : 1274 − 1274 = **0**. Shopify avait DÉJÀ étalé la remise
           sur les lignes, donc l'écart que je cherchais n'existait plus au
           moment où je le cherchais, et la répartition ne s'activait jamais.
           C'est exactement ce qu'il voyait : 50,99 € et 21,25 €, inchangés.
         ➜ La remise totale se lit sur les PRIX PLEINS : somme des
           `original_line_price` moins le total facturé. Elle est réelle quelle
           que soit la façon dont Shopify l'a déjà ventilée. */
      /* ═══ CHAQUE ARTICLE AU PRIX QU'IL A COÛTÉ EN PLUS — 30/08, 08h30 ═══
         Sa correction : « c'est en échelle · le produit ajouté il est ajouté au
         prix qui est = à la différence justement ».

         CE QUE JE FAISAIS ET QUI ÉTAIT FAUX : j'étalais la remise à parts égales
         sur les articles ajoutés. Sur son panier de 3 maillots : 24,99 · 19,37 ·
         19,37. La somme tombait juste, mais aucun de ces nombres ne correspondait
         à quoi que ce soit qu'on lui ait promis.

         CE QU'IL DEMANDE, ET C'EST LA SEULE VERSION COHÉRENTE : le prix d'un
         article est ce qu'il a coûté EN PLUS au moment où on l'a ajouté —
         total(n) − total(n−1). Sur le même panier : 24,99 · 17,50 · 21,24.
         Et 17,50 €, c'est EXACTEMENT le prix que le bloc « 2e maillot » annonce.
         Le client voit dans son panier le nombre sur lequel il a cliqué.

         ⚠️ LE 3e PEUT COÛTER PLUS CHER QUE LE 2e, et ce n'est pas un défaut :
           tant que le palier ne change pas, la remise reste la même en valeur et
           se dilue sur un article de plus. Le jour où le client atteint le palier
           suivant, le prix marginal rebaisse. C'est ce que la barre de paliers
           lui montre déjà.

         ⚠️ ON CUMULE LES PRIX PLEINS DANS L'ORDRE D'AJOUT — donc en partant de la
           FIN de cart.items, que Shopify trie du plus récent au plus ancien.
         ⚠️ La somme reste exactement le total facturé : le dernier article
           absorbe le reliquat de centimes de l'arrondi. */
      const pleinTotal = (cart.items || []).reduce((n, i) => n + (i.original_line_price || i.final_line_price || 0), 0);
      const remisePanierCents = Math.max(0, pleinTotal - (cart.total_price || 0));
      let reportRemise = null;
      if (!cartReallocation && remisePanierCents > 0 && (cart.items || []).length >= 2) {
        const taux = remisePanierCents / pleinTotal;
        /* du plus ancien au plus récent */
        const chrono = cart.items.slice().reverse();
        const parLigne = new Map();
        let cumulPlein = 0, totalPrecedent = 0, cumulAttribue = 0;
        /* ⛔ UNE MARGE NÉGATIVE EFFACE LA REMISE — 31/08, 03h25.
             Reproduit : maillot 24,99 + veste 59,99, code `onixmgoat` à 45 %.
             Le plus ancien (la veste) garde 59,99 ; le total remisé vaut 46,74,
             donc la marge du maillot vaut 46,74 − 59,99 = **−13,25**. Le prix de
             ligne devenait négatif, était ramené à 0,00 € à l'affichage, et
             l'écran montrait 59,99 + 0,00 = 59,99 pour un panier facturé 46,74.
             **13,25 € de remise invisibles.**
             Le garde `sommeReconstituee === total` ne le voyait pas : il
             calculait avec la marge NÉGATIVE, pas avec le 0 réellement affiché.
             ➜ On borne chaque marge à [0, plein] — une ligne ne peut ni coûter
               moins que rien, ni plus que son prix catalogue — puis on reporte
               le manque sur les lignes PRÉCÉDENTES, de la plus récente vers la
               plus ancienne. La somme reste exacte, et la lecture reste celle
               qu'il a demandée le 30/08 : les articles ajoutés sont les moins
               chers, jusqu'à être offerts.
             ⚠️ Si le report ne suffit pas (remise supérieure au panier entier),
               on ABANDONNE l'échelle et on laisse la répartition de Shopify :
               un affichage moins parlant vaut mieux qu'un affichage faux. */
        const marges = [];
        chrono.forEach((it, idx) => {
          const plein = it.original_line_price || it.final_line_price || 0;
          cumulPlein += plein;
          const totalIci = idx === 0 ? cumulPlein : Math.round(cumulPlein * (1 - taux));
          let marge = totalIci - totalPrecedent;
          if (idx === chrono.length - 1) marge = (cart.total_price || 0) - totalPrecedent;
          marges.push({ it, plein, marge });
          totalPrecedent = totalPrecedent + marge;
        });
        /* borne, puis report du manque vers les lignes précédentes */
        let dette = 0;
        for (let i = marges.length - 1; i >= 0; i--) {
          const m = marges[i];
          let v = m.marge - dette;
          dette = 0;
          if (v < 0) { dette = -v; v = 0; }
          else if (v > m.plein) { dette = -(m.plein - v); v = m.plein; }
          m.marge = v;
        }
        for (const m of marges) {
          parLigne.set(m.it.key, m.plein - m.marge);
          cumulAttribue += m.plein - m.marge;
        }
        /* ⚠️ LE GARDE SE CALCULE SUR CE QUI SERA AFFICHÉ, pas sur l'intention :
             c'est précisément l'écart entre les deux qui a laissé passer le
             défaut du 31/08. On somme les marges bornées. */
        const sommeReconstituee = marges.reduce((n, m) => n + m.marge, 0);
        if (sommeReconstituee === cart.total_price) {
          reportRemise = parLigne;
        } else {
          console.warn('[onix] échelle abandonnée : ' + sommeReconstituee + ' ≠ ' +
            cart.total_price + ' — on garde la répartition de Shopify.');
        }
      }

      const buildItemPricing = (item) => {
        const isBundle = !!item._bundleMerged || (bundleActive && item.properties && item.properties._offer_bundle === 'true');
        let lineTotal;
        if (cartReallocation && isBundle) {
          /* Étalement uniforme (-40%) : chaque unité au même prix, le reste de
             centimes (≤ qty−1) posé sur la 1re ligne. Somme = total bundle. */
          lineTotal = cartReallocation.perUnit * item.quantity
            + (item.variant_id === cartReallocation.holderVariantId ? cartReallocation.remainder : 0);
        } else if (cartReallocation) {
          lineTotal = item.quantity * item.price;
        } else if (reportRemise) {
          /* prix PLEIN moins la part de remise attribuée à cette ligne : le plus
             ancien article n'en reçoit aucune et garde donc son prix catalogue. */
          lineTotal = (item.original_line_price || item.final_line_price || 0)
            - (reportRemise.get(item.key) || 0);
        } else {
          lineTotal = item.final_line_price;
        }
        const unitPrice = item.quantity > 0 ? Math.round(lineTotal / item.quantity) : item.final_price;
        const cap = capMap[item.variant_id];
        let effectiveCompareUnit;
        if (isBundle) {
          effectiveCompareUnit = cap && cap > item.final_price ? cap : item.final_price;
        } else {
          effectiveCompareUnit = cap && cap > item.final_price ? cap : (item.original_price && item.original_price > item.final_price ? item.original_price : 0);
        }
        const compareTotal = effectiveCompareUnit ? effectiveCompareUnit * item.quantity : 0;
        return { isBundle, lineTotal, unitPrice, effectiveCompareUnit, compareTotal, fullUnitAttr: item.price };
      };

      const renderBundleCard = () => {
        if (bundleRenderItems.length === 0) return '';
        let bundleSumCents = 0;
        const rowsHtml = bundleRenderItems.map((item) => {
          const { lineTotal, unitPrice, effectiveCompareUnit, compareTotal, fullUnitAttr } = buildItemPricing(item);
          bundleSumCents += lineTotal;
          const displayKey = item._bundleMerged ? `bundle:${item.variant_id}` : item.key;
          const bundleKeysAttr = item._bundleKeys
            ? ` data-bundle-keys='${escapeHtml(JSON.stringify(item._bundleKeys))}' data-bundle-variant="${escapeHtml(String(item.variant_id))}"`
            : '';
          const media = itemImage(item)
            ? `<a class="onix-bundle-card__media" href="${escapeHtml(item.url)}"><img src="${escapeHtml(itemImage(item))}" alt="${escapeHtml(item.product_title)}"></a>`
            : `<div class="onix-bundle-card__media"></div>`;
          const sizeValue = itemSizeValue(item);
          const sizePill = sizeValue
            ? `<span class="onix-bundle-card__size">${escapeHtml(sizeValue)}</span>`
            : '';
          const comparePriceHtml = compareTotal
            ? `<span class="onix-bundle-card__compare" data-unit-compare="${effectiveCompareUnit}">${formatMoney(compareTotal)}</span>`
            : '';
          return buildBundleRowHtml({
            displayKey,
            variantId: item.variant_id,
            extraArticleAttrs: bundleKeysAttr,
            mediaHtml: media,
            url: item.url,
            title: item.product_title,
            removeBtnHtml: `<button class="onix-bundle-card__remove" type="button" data-cart-remove data-line-key="${escapeHtml(displayKey)}" aria-label="${escapeHtml(t.remove)}">${BUNDLE_REMOVE_SVG}</button>`,
            sizePillHtml: sizePill,
            quantity: item.quantity,
            comparePriceHtml,
            priceAttrs: ` data-unit-price="${unitPrice}" data-line-total="${lineTotal}" data-full-unit="${fullUnitAttr}" data-bundle="1"`,
            priceCents: lineTotal,
          });
        }).join('');
        return buildBundleSectionHtml(rowsHtml, bundleSumCents);
      };

      const bundleCardHtml = renderBundleCard();

      /* La carte du pack. Elle réutilise le dessin de la carte d'offre, avec
         son propre badge et sa propre couleur — c'est le MÊME composant, pas
         une copie : une divergence de balisage entre deux cartes jumelles est
         exactement ce qui avait produit le bug « 15€/15€ » plus haut. */
      const renderPackCard = (packRenderItems) => {
        if (!packRenderItems || packRenderItems.length === 0) return '';
        /* ⛔ `final_line_price` NE SUFFIT PAS, ET C'EST LE PIÈGE — 29/08.
           Le code onix3 est une remise de NIVEAU PANIER, pas de ligne : Shopify
           laisse chaque `final_line_price` au plein tarif et pose la réduction
           sur le total. La carte affichait donc 119,98 € et deux lignes à
           59,99 € alors que la caisse facturait 101,99 €.
           ➜ On additionne les deux sources : ce qui est alloué à la ligne, PLUS
             la part proportionnelle de la remise panier. Sur un panier qui ne
             contient que le pack, cela retombe exactement sur cart.total_price ;
             sur un panier mixte, chaque article porte sa juste part. */
        const pleinPack = packRenderItems.reduce((s2, i) => s2 + i.original_line_price, 0);
        const remiseLignePack = packRenderItems.reduce((s2, i) =>
          s2 + (i.line_level_discount_allocations || []).reduce((x, a2) => x + (a2.amount || 0), 0), 0);
        const pleinPanier = (cart.items || []).reduce((s2, i) => s2 + i.original_line_price, 0);
        const remiseLigneTotale = (cart.items || []).reduce((s2, i) =>
          s2 + (i.line_level_discount_allocations || []).reduce((x, a2) => x + (a2.amount || 0), 0), 0);
        /* ce qui reste après les remises de ligne est la remise de PANIER */
        const remisePanier = Math.max(0, (cart.total_discount || 0) - remiseLigneTotale);
        const partPack = pleinPanier > 0 ? (pleinPack - remiseLignePack) / Math.max(1, pleinPanier - remiseLigneTotale) : 0;
        const totalPack = Math.max(0, Math.round(pleinPack - remiseLignePack - remisePanier * partPack));
        /* ⚠️ TOUTE LA REMISE SUR LA 2ᵉ LIGNE, comme sur la fiche produit.
           Sa règle du 29/08 : « affiche le prix de la 2ᵉ comme si c'était elle
           la moins chère ». Deux lignes à 50,99/51,00 obligent le client à
           faire une soustraction ; 59,99 puis 42,00 montrent l'économie là où
           elle se décide. Le total est rigoureusement le même. */
        let reste = totalPack;
        const n = packRenderItems.length;
        const rowsHtml = packRenderItems.map((item, idx) => {
          const dernier = idx === n - 1;
          const lineTotal = dernier ? Math.max(0, reste) : item.original_line_price;
          if (!dernier) reste -= lineTotal;
          const media = itemImage(item)
            ? `<a class="onix-bundle-card__media" href="${escapeHtml(item.url)}"><img src="${escapeHtml(itemImage(item))}" alt="${escapeHtml(item.product_title)}"></a>`
            : `<div class="onix-bundle-card__media"></div>`;
          const sizeValue = itemSizeValue(item);
          const sizePill = sizeValue ? `<span class="onix-bundle-card__size">${escapeHtml(sizeValue)}</span>` : '';
          const compare = lineTotal < item.original_line_price
            ? `<span class="onix-bundle-card__compare">${formatMoney(item.original_line_price)}</span>` : '';
          return buildBundleRowHtml({
            displayKey: item.key,
            variantId: item.variant_id,
            mediaHtml: media,
            url: item.url,
            /* ⚠️ LE CORIS SEUL, PAS LE TITRE ENTIER — 29/08.
               « Brésil – Veste Coupe-Vent à Capuche – Gris Bleu & Vert Citron »
               ne tient pas sur une rangée : tronqué, il donne « Brésil – Veste… »
               sur les DEUX lignes, donc deux libellés identiques pour deux
               produits différents. Or ce qui distingue les vestes d'un pack,
               c'est exactement la partie coupée. On garde donc le dernier
               segment du titre — le coloris — et la photo dit le reste.
               ⚠️ Repli sur le titre complet si le nom ne suit pas la convention
                 « Marque – Type – Coloris » : mieux vaut un titre long qu'un
                 libellé vide sur un produit nommé autrement. */
            title: (() => {
              const parts = String(item.product_title || '').split('–');
              const dernier = parts.length > 1 ? parts[parts.length - 1].trim() : '';
              return dernier || item.product_title;
            })(),
            removeBtnHtml: `<button class="onix-bundle-card__remove" type="button" data-cart-remove data-line-key="${escapeHtml(item.key)}" aria-label="${escapeHtml(t.remove)}">${BUNDLE_REMOVE_SVG}</button>`,
            sizePillHtml: sizePill,
            quantity: item.quantity,
            comparePriceHtml: compare,
            priceAttrs: ` data-line-total="${lineTotal}"`,
            priceCents: lineTotal,
          });
        }).join('');
        const eco = pleinPack - totalPack;
        const entete = buildBundleSectionHtml(rowsHtml, totalPack, t.packLabel || 'Pack 2 vestes', 'onix-bundle-card--pack');
        /* l'économie, posée sous l'en-tête — c'est l'argument, il ne se déduit pas */
        return eco > 0
          ? entete.replace('</header>', `</header><p class="onix-bundle-card__save">${escapeHtml((t.packSave || 'Tu économises @@').replace('@@', formatMoney(eco)))}</p>`)
          : entete;
      };
      const packCardHtml = [...parPack.values()].map((lignes) => renderPackCard(lignes)).join('');

      /* ⛔ L'ORDRE D'AFFICHAGE SUIT L'ORDRE D'AJOUT — 30/08, 08h10. Sa remarque :
           « t'es censé afficher les cartes dans l'ordre d'ajout au panier, alors
             que là le dernier maillot ajouté il est tout en haut ».

         VÉRIFIÉ : j'ajoute un maillot AVEC flocage, puis un SANS. L'API rend
         « sans flocage » en premier et le tiroir l'affiche en premier. Shopify
         met le PLUS RÉCENT en tête de `cart.items`, et le thème rendait dans cet
         ordre-là sans le retourner.

         ⚠️ ON N'INVERSE QUE L'AFFICHAGE. Le calcul qui décide quel article porte
           la remise lit `cart.items` directement (le plus ancien = le dernier du
           tableau) : il ne dépend pas de l'ordre de rendu, et il ne doit surtout
           pas en dépendre. Les deux restent donc cohérents — vérifié après coup,
           pas supposé.
         ⚠️ LES CARTES DE GROUPE RESTENT EN TÊTE (offre découverte, pack 2 vestes).
           Ce sont des regroupements, pas des lignes : les mêler à l'ordre
           chronologique ferait sauter une carte de quatre articles au milieu de
           la liste à chaque ajout. Choix assumé, signalé à l'arbitrage. */
      /* ⛔ TRI PAR PRIX DÉCROISSANT — 30/08, 08h40. Sa règle, dans ses mots :
           « c'est plus logique d'afficher du plus cher au moins cher pour
             faciliter la lecture, c'est la 2e condition qui gagne sur la date
             d'ajout au panier du produit ».
         L'ordre d'ajout reste le repli quand deux lignes valent le même prix —
         sinon deux articles identiques sauteraient de place à chaque rendu.

         ⚠️ ON TRIE L'AFFICHAGE, PAS LE CALCUL. L'échelle marginale se construit
           dans l'ordre d'AJOUT : le premier article garde son prix plein, chaque
           suivant porte ce qu'il a coûté en plus. Trier avant de calculer ferait
           dépendre les prix de leur propre ordre — le calcul se mordrait la
           queue et changerait à chaque rendu.
           C'est la même séparation que pour l'ordre chronologique deux heures
           plus tôt : les données décident des montants, l'affichage décide de la
           place. Les mélanger est ce qui a produit les trois versions ratées.

         ⚠️ LE PRIX DE TRI EST CELUI QU'ON VA AFFICHER, pas `final_line_price` :
           avec l'échelle, les deux diffèrent, et trier sur le mauvais donnerait
           un ordre qui ne correspond pas aux nombres à l'écran. */
      const prixAffiche = (it) => (reportRemise
        ? (it.original_line_price || it.final_line_price || 0) - (reportRemise.get(it.key) || 0)
        : (it.final_line_price || 0));
      const ordreAjout = new Map(cart.items.slice().reverse().map((it, n) => [it.key, n]));

      remplacerCorps(drawerBody, bundleCardHtml + packCardHtml + regularRenderItems
        .slice()
        .sort((a, b) => (prixAffiche(b) - prixAffiche(a))
          || ((ordreAjout.get(a.key) ?? 0) - (ordreAjout.get(b.key) ?? 0)))
        .map((item) => {
          const isBundle = !!item._bundleMerged || (bundleActive && item.properties && item.properties._offer_bundle === 'true');
          /* Regular (non-bundle) items. Quand un bundle est actif, l'offre est
             non-cumulable → les articles hors bundle sont à plein tarif ; sinon
             on suit final_line_price (reflète un éventuel code de palier). */
          let lineTotal;
          if (cartReallocation) {
            lineTotal = item.quantity * item.price;
          } else {
            lineTotal = item.final_line_price;
          }
          /* ⚠️ `final_line_price` N'INCLUT PAS LES REMISES DE PANIER — 23/08.
             Signalé par lui : « dans le panier quand une offre est appliquée, le
             prix sur les cartes produit se met pas à jour, ça reste celui de
             base ». Mesuré sur un panier réel avec ONIX3 : la ligne restait à
             44,00 € pendant que le pied affichait 37,40 €.

             Shopify distingue deux niveaux. Une remise de LIGNE est déjà
             déduite de `final_line_price`. Une remise de PANIER ne l'est pas :
             elle vit dans `cart_level_discount_applications`… mais Shopify en
             ALLOUE la part à chaque ligne, dans `item.discounts[].amount`.
             Le chiffre juste existe donc déjà, il n'était pas lu.

             ⚠️ ET IL NE FAUT PAS DOUBLE-COMPTER : une remise de ligne apparaît
               dans `discounts` ET est déjà retirée de `final_line_price`. On ne
               soustrait donc que l'écart entre les deux totaux. Sans remise,
               l'écart vaut 0 et rien ne change. */
          if (!cartReallocation) {
            const totalDiscounts = (item.discounts || [])
              .reduce((s, d) => s + (Number(d.amount) || 0), 0);
            const dejaDeduit = (item.line_level_discount_allocations || [])
              .reduce((s, d) => s + (Number(d.amount) || 0), 0);
            const remisePanier = Math.max(0, totalDiscounts - dejaDeduit);
            if (remisePanier > 0) lineTotal = Math.max(0, lineTotal - remisePanier);
          }

          /* ⛔ LA REMISE EST PORTÉE PAR LES ARTICLES AJOUTÉS — 30/08, 08h35.
               Sa demande : « visuellement faut faire comme si c'était toujours
               les produits en plus qui sont moins chers ».
               Shopify étale la remise au prorata : son écran montrait la veste
               promise à 47,24 € affichée 50,99 €, et le maillot déjà présent
               tombé de 24,99 à 21,25 €. Le total était juste ; la répartition
               contredisait la promesse, et le nombre sur lequel il avait cliqué
               n'apparaissait nulle part.

             ⚠️ C'EST LE TROISIÈME ENDROIT QUE J'ESSAIE. J'avais d'abord modifié
               `buildItemPricing` — qui n'est appelé QUE pour les cartes de
               l'offre découverte. Les lignes normales, celles de son panier, ne
               passent pas par elle : elles calculent leur prix ICI. Ma
               correction était juste et jamais atteinte, et je la lui ai
               annoncée deux fois comme faite.
               ➜ Avant de corriger un calcul, vérifier QUI l'exécute pour le cas
                 qu'on veut réparer. Un code juste au mauvais endroit se comporte
                 exactement comme un code absent. */
          if (reportRemise && reportRemise.has(item.key)) {
            lineTotal = Math.max(0,
              (item.original_line_price || item.final_line_price || 0) - reportRemise.get(item.key));
          } else if (reportRemise) {
            /* article le plus ancien : il garde son prix catalogue */
            lineTotal = item.original_line_price || item.final_line_price || lineTotal;
          }
          const unitPrice = item.quantity > 0 ? Math.round(lineTotal / item.quantity) : item.final_price;
          const fullUnitAttr = item.price;
          const bundleKeysAttr = item._bundleKeys
            ? ` data-bundle-keys='${escapeHtml(JSON.stringify(item._bundleKeys))}' data-bundle-variant="${escapeHtml(String(item.variant_id))}"`
            : '';
          const displayKey = item._bundleMerged ? `bundle:${item.variant_id}` : item.key;

          const cap = capMap[item.variant_id];
          let effectiveCompareUnit;
          if (isBundle) {
            /* Strike-through shows original unit × qty */
            effectiveCompareUnit = cap && cap > item.final_price ? cap : item.final_price;
          } else {
            /* ⚠️ On compare au prix RÉELLEMENT affiché (`unitPrice`), pas à
               `final_price` : depuis la correction ci-dessus, les deux
               diffèrent dès qu'une remise de panier s'applique, et se comparer
               à `final_price` faisait disparaître le barré au moment précis où
               il y avait le plus à montrer. */
            effectiveCompareUnit = cap && cap > unitPrice
              ? cap
              : (item.original_price && item.original_price > unitPrice ? item.original_price : 0);
          }
          const compareTotal = effectiveCompareUnit ? effectiveCompareUnit * item.quantity : 0;
          const comparePrice = compareTotal
            ? `<span class="onix-cart-item__compare" data-unit-compare="${effectiveCompareUnit}">${formatMoney(compareTotal)}</span>`
            : "";

          const sizeValue0 = itemSizeValue(item);
          /* T6 — LA TAILLE EST POSÉE SUR LA PHOTO, pas sur une ligne à elle.
             Elle libère une rangée entière de la carte, et elle se lit là où
             l'œil va déjà : sur l'image du maillot. */
          const pastilleTaille = sizeValue0
            ? `<span class="onix-cart-item__size-pill">${escapeHtml(sizeValue0)}</span>`
            : "";
          const media = itemImage(item)
            ? `<a class="onix-cart-item__media" href="${escapeHtml(item.url)}"><img class="onix-cart-item__image" src="${escapeHtml(itemImage(item))}" alt="${escapeHtml(item.product_title)}">${pastilleTaille}</a>`
            : `<div class="onix-cart-item__media">${pastilleTaille}</div>`;

          const nextMinus = Math.max(0, item.quantity - 1);

          return `
          <article class="onix-cart-item${isBundle ? ' onix-cart-item--bundle' : ''}" data-cart-item-key="${escapeHtml(displayKey)}" data-qte="${item.quantity}" data-variant-id="${escapeHtml(String(item.variant_id))}"${isBundle ? ' data-bundle="1"' : ''}${bundleKeysAttr}>
            ${media}
            <div class="onix-cart-item__content">
              <div class="onix-cart-item__topline">
                <h3 class="onix-cart-item__title"><a href="${escapeHtml(item.url)}">${(() => {
                  const d = titreEnDeux(item.product_title);
                  return `<span class="onix-cart-item__club">${escapeHtml(d.club)}</span>`
                    + (d.reste ? `<span class="onix-cart-item__sous">${escapeHtml(d.reste)}</span>` : '');
                })()}</a></h3>
              </div>
              ${flocageEnLigne(item.properties)}
              <div class="onix-cart-item__bottomline">
                <div class="onix-cart-item__quantity" aria-label="${escapeHtml(t.quantity)}">
                  <button type="button" class="onix-cart-item__moins" data-cart-quantity data-cart-direction="-1" data-line-key="${escapeHtml(displayKey)}" data-quantity="${nextMinus}" aria-label="${escapeHtml(item.quantity === 1 ? t.remove : t.quantityDown)}"><span class="onix-cart-item__moins-signe" aria-hidden="true">-</span><svg class="onix-cart-item__moins-corbeille" aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg></button>
                  <span>${item.quantity}</span>
                  <button type="button" data-cart-quantity data-cart-direction="1" data-line-key="${escapeHtml(displayKey)}" data-quantity="${item.quantity + 1}">+</button>
                </div>
                <div class="onix-cart-item__pricing">
                  <span class="onix-cart-item__price" data-unit-price="${unitPrice}" data-line-total="${lineTotal}" data-full-unit="${fullUnitAttr}"${isBundle ? ' data-bundle="1"' : ''}>${formatMoney(lineTotal)}</span>
                  ${comparePrice}
                </div>
                <button class="onix-cart-item__remove" type="button" data-cart-remove data-line-key="${escapeHtml(displayKey)}" aria-label="${escapeHtml(t.remove)}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg></button>
              </div>
            </div>
          </article>
        `;
        })
         .join(""));

      // Update compare_at_price map for newly added products
      const missingHandles = [...new Set(cart.items.filter(i => !(i.variant_id in capMap)).map(i => i.handle))];
      if (missingHandles.length) {
        Promise.all(missingHandles.map(h => fetch(`${rootPath}products/${h}.js`).then(r => r.json()).catch(() => null)))
          .then(products => {
            let updated = false;
            products.forEach(p => {
              if (!p) return;
              p.variants.forEach(v => { if (v.compare_at_price) { capMap[v.id] = v.compare_at_price; updated = true; } });
            });
            window.__onixCompareAtPrices = capMap;
            if (updated) safeRefreshCart();
          });
      }

    };

    const fetchCart = async () => {
      const response = await loggedFetch(`${rootPath}cart.js`, {
        headers: { Accept: "application/json" },
      });
      return response.json();
    };

    /* Collect all discount titles currently active on a cart (cart-level
       + line-level) as uppercase strings. */
    const collectDiscountTitles = (cart) => {
      const out = [];
      (cart.cart_level_discount_applications || []).forEach(a => { if (a.title) out.push(a.title.toUpperCase()); });
      (cart.items || []).forEach(i => {
        (i.discounts || []).forEach(d => { if (d.title) out.push(d.title.toUpperCase()); });
        (i.line_level_discount_allocations || []).forEach(a => {
          if (a.discount_application && a.discount_application.title) out.push(a.discount_application.title.toUpperCase());
        });
      });
      return out;
    };

    /* Resolve the offer discount code (e.g. ONIIX) from the popup DOM so we
       can detect and strip it from the session whenever bundle items aren't
       present. Read-only access to #onix-offer — does not touch the popup. */
    const getOfferCode = () => {
      let code = (window.__onixOfferDiscountCode || '').toUpperCase();
      if (!code) {
        const el = document.getElementById('onix-offer');
        if (el && el.dataset.discountCode) code = el.dataset.discountCode.toUpperCase();
      }
      return code;
    };

    /* Clear a specific discount code from the Shopify session. */
    const clearSessionDiscount = async () => {
      try {
        await loggedFetch(`${rootPath}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'discount=',
        });
      } catch (e) {
        console.warn('[onix-cart] clearSessionDiscount failed', e);
      }
    };

    /* `preCart` lets a writer hand off the cart it just received from
       /cart/change.js or /cart/update.js (both return the full cart in the
       same shape as /cart.js) so we skip a redundant /cart.js round-trip.
       The bundle-orphan branch below still re-fetches when it actually
       mutates the cart, so server-truth is preserved. */
    const refreshCart = async (preCart) => {
      let cart = preCart || await fetchCart();
      const bundleItems = cart.items.filter(i => i.properties && i.properties._offer_bundle === 'true');
      let bundleItemsCount = bundleItems.length;
      /* On compte la QUANTITÉ (pas le nombre de lignes) : 2 maillots du même
         variant sont mergés par Shopify en une seule ligne qty 2 — la longueur
         dirait « 1 » à tort et casserait un bundle 2 valide. */
      const bundleQty = bundleItems.reduce((s, i) => s + i.quantity, 0);

      /* L'offre découverte n'est valide qu'à EXACTEMENT 2 ou 4 maillots. Si une
         suppression laisse un compte INVALIDE (1, 3, 5…), on retire le flag
         _offer_bundle de TOUS les items concernés → ils redeviennent des
         maillots normaux à plein prix, la bundle card disparaît et le code offre
         (devenu non applicable) est nettoyé. Sans ça, le client verrait une
         « OFFRE DÉCOUVERTE » au prix plein (ex : 4 maillots → on en supprime 1 →
         3 maillots à 74,97€ avec un code mort en session). cart/change.js ne
         peut pas muter les propriétés → on retire les lignes puis on les ré-ajoute
         nettoyées. */
      if (bundleQty > 0 && bundleQty !== 2 && bundleQty !== 4) {
        try {
          const updates = {};
          const reAdds = [];
          bundleItems.forEach((it) => {
            updates[it.key] = 0;
            const cleaned = {};
            Object.entries(it.properties || {}).forEach(([k, v]) => {
              /* Retire TOUS les marqueurs d'offre (_offer_bundle, _bundle_session,
                 _offer_code, _offer_qty…) pour une ligne 100% normale ; on garde
                 les éventuelles props client (flocage Nom/Numéro…). */
              if (!/^_(offer|bundle)/.test(k)) cleaned[k] = v;
            });
            reAdds.push({ id: Number(it.variant_id), quantity: it.quantity, properties: cleaned });
          });
          await loggedFetch(`${rootPath}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
          await loggedFetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: reAdds }),
          });
          /* Efface le code offre devenu invalide, puis force un render pour que
             les maillots sortent immédiatement de la bundle card (chemin
             server-authoritative, pas en course avec le spam de clics). */
          try { await clearSessionDiscount(); } catch (e) { console.warn('[onix-cart] clearSessionDiscount bundle-strip failed', e); }
          cart = await fetchCart();
          bundleItemsCount = 0;
          if (window.__onixResetOfferBundle) window.__onixResetOfferBundle();
          delete window.__onixOfferDiscountCode;
          renderCart(cart);
          /* Le compteur total (item_count) NE change PAS pendant le strip
             (ex: 3 maillots bundle → 3 maillots normaux), donc le promo-suggest
             ne se ré-déclenche pas via son observer de badge. Or pendant l'état
             intermédiaire son compteur NON-bundle valait 0 → il a masqué son
             popup de palier sans le ré-afficher. On force une ré-évaluation à
             l'état FINAL stable (setTimeout, pas rAF : robuste onglet caché) pour
             que le popup « applique ONIX4 » réapparaisse sur les maillots restants. */
          const _strippedCart = cart;
          setTimeout(function () {
            try { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: _strippedCart } })); } catch (e) {}
          }, 500);
          return cart;
        } catch (e) {
          console.warn('[onix-cart] bundle strip failed', e);
        }
      }
      const hasBundleItems = bundleItemsCount > 0;

      /* La gestion de la session de codes (bundle + paliers) est centralisée
         dans cart-drawer-promo-suggest.js (settling anti-spam). On ne garde
         ici que le nettoyage local de l'UI du popup « offre découverte » pour
         qu'elle se réinitialise quand le bundle quitte le panier. */
      if (!hasBundleItems) {
        if (window.__onixResetOfferBundle) window.__onixResetOfferBundle();
        delete window.__onixOfferDiscountCode;
      }

      /* Spam-safe: never render over in-flight optimistic updates. The
         pending flush will call renderCart when the bucket drains. */
      if (!hasPendingWrites()) renderCart(cart);
      return cart;
    };

    /* Coalesce concurrent refreshCart calls: if one is in-flight, callers
       share the same promise instead of triggering a second /cart.js fetch.
       A refresh requested while another is running is replayed once (so we
       don't miss a write that landed mid-flight). */
    let _refreshInFlight = null;
    let _refreshPending = false;
    const safeRefreshCart = (preCart) => {
      if (_refreshInFlight) {
        _refreshPending = true;
        return _refreshInFlight;
      }
      _refreshInFlight = (async () => {
        try { await refreshCart(preCart); } catch (e) { console.warn('[onix-cart] refresh failed', e); }
      })().finally(() => {
        _refreshInFlight = null;
        if (_refreshPending) {
          _refreshPending = false;
          safeRefreshCart();
        }
      });
      return _refreshInFlight;
    };

    /* ── Optimistic UI helpers ── */
    /* CE RETRAIT FAIT-IL TOMBER LA REMISE ?
       Le palier de référence est celui du code que Shopify applique RÉELLEMENT
       (`_appliedTier`, posé par renderCart d'après cart.js), jamais « le plus
       haut que le panier atteint ». Avec onix3 (palier 2) dans un panier de 4,
       la version précédente prenait le palier 3 pour référence et annonçait la
       perte de la remise à 4→3, alors qu'onix3 tient jusqu'à 2.

       ⚠️ ET ELLE NE LIT RIEN QUE SON APPELANT VIENT D'ÉCRIRE. C'est ce qui la
         rendait inerte : `optimisticQuantity` écrit `article.dataset.qte`
         AVANT d'appeler le prédicteur, qui resommait ces mêmes `data-qte`. Le
         total lu était donc déjà celui d'APRÈS le clic, et on lui retranchait
         `delta` une seconde fois. Sur 2 maillots avec onix3 il lisait 1, ne
         trouvait plus aucun palier atteint, et répondait « rien à perdre » :
         le tiroir gardait 21,25 € pendant que Shopify facturait 24,99 €.
       ➜ On somme donc le RESTE du panier — la ligne en cours de changement est
         exclue — et ses deux quantités arrivent par argument. L'ordre des
         écritures n'a plus d'effet sur le résultat.
       Mesuré au banc sur ce fichier même, le 23/08 : 2→1 rendait 21,25 € au
       lieu de 24,99 €, et 4→3 rendait 24,99 € au lieu de 21,24 €. */
    const remisePerdueApres = (article, qteAvant, qteApres) => {
      if (qteApres >= qteAvant) return false;
      /* Pas de code de palier actif, ou un bundle en cours : rien à prédire.
         Même condition que le sous-total optimiste, donc la ligne et le total
         ne peuvent pas se contredire. */
      if (!_appliedTier || _cartHasBundle) return false;
      /* Les lignes qui s'effacent (`data-removing`) ne comptent plus, et une
         ligne de bundle n'entre pas dans un palier. */
      const reste = [...document.querySelectorAll('.onix-cart-item[data-qte]:not([data-removing])')]
        .filter((n) => n !== article && n.dataset.bundle !== '1')
        .reduce((s, n) => s + (Number(n.dataset.qte) || 0), 0);
      return (reste + qteAvant) >= _appliedTier.qty && (reste + qteApres) < _appliedTier.qty;
    };

    /* Un palier est une remise de PANIER : quand il tombe, TOUTES les lignes
       repassent au plein tarif, pas seulement celle qu'on vient de changer.
       Sans ça, un panier de 2 lignes A(2)+B(1) sous onix4 (palier 3) laissait B
       affiché remisé pendant ~1 s après le retrait qui a fait tomber le palier —
       le même prix qui remonte tout seul, une ligne plus bas. */
    const passerLignesAuPrixPlein = (sauf) => {
      document.querySelectorAll('.onix-cart-item[data-qte]:not([data-removing])').forEach((n) => {
        if (n === sauf || n.dataset.bundle === '1') return;
        const p = n.querySelector('.onix-cart-item__price');
        if (!p || !p.dataset.unitPrice) return;
        const uRemise = Number(p.dataset.unitPrice);
        const uPlein = Number(p.dataset.fullUnit) || uRemise;
        if (uPlein <= uRemise) return;               /* ligne déjà au plein tarif */
        const q = Number(n.dataset.qte) || 0;
        p.dataset.unitPrice = String(uPlein);
        animatePrice(p, uRemise * q, uPlein * q, 300);
      });
    };

    const optimisticQuantity = (article, newQty, oldQty) => {
      const qtySpan = article.querySelector('.onix-cart-item__quantity > span');
      if (qtySpan) qtySpan.textContent = newQty;
      /* ⚠️ Q4 — `data-qte` DOIT SUIVRE ICI, et c'est le seul endroit à tenir.
           Ce chemin change la quantité SANS re-rendre la carte : sans cette
           ligne, le « − » resterait une corbeille à 2 et la vraie corbeille
           resterait cachée. Tout le reste (quel glyphe, quelle corbeille) est
           décidé en CSS d'après cet attribut — il n'y a donc qu'une seule
           écriture à ne pas oublier, pas quatre. */
      article.dataset.qte = newQty;
      const moins = article.querySelector('.onix-cart-item__moins');
      if (moins) moins.setAttribute('aria-label', newQty === 1 ? t.remove : t.quantityDown);
      const priceEl = article.querySelector('.onix-cart-item__price');
      if (priceEl && priceEl.dataset.bundle === '1' && priceEl.dataset.lineTotal) {
        /* Bundle line: each extra unit above qty 1 costs the full variant
           price (not the discounted bundle unit). Read dynamically so it
           adapts to variant price changes without hardcoded values. */
        const delta = Number(priceEl.dataset.fullUnit) || BUNDLE_EXTRA_FALLBACK_CENTS;
        const oldTotal = Number(priceEl.dataset.lineTotal);
        const newTotal = oldTotal + (newQty - oldQty) * delta;
        priceEl.dataset.lineTotal = String(newTotal);
        animatePrice(priceEl, oldTotal, newTotal, 300);
      } else if (priceEl && priceEl.dataset.unitPrice) {
        /* ⚠️ QUEL PRIX UNITAIRE ? PAS TOUJOURS CELUI DE LA LIGNE.
           `data-unit-price` est le prix unitaire TEL QUE FACTURÉ AU MOMENT DU
           RENDU — donc remisé si un palier est actif (21,25 € à 2 maillots avec
           onix3). Baisser la quantité sous le minimum du palier fait tomber la
           remise côté Shopify : l'unité redevient pleine.
           Sans prédiction, le client voit « 21,25 € » pendant ~1 s, puis le prix
           REMONTE à 24,99 € quand le serveur répond. Constaté par le
           propriétaire le 23/08 : « le prix du maillot redescendait à 21,25 et
           après il remontait à 24,99 ».
           Un prix qui monte tout seul après une action du client est pire qu'un
           prix affiché tard : il ressemble à une facturation surprise.
           ➜ Quand le retrait fait repasser sous le palier actif, on affiche
           d'emblée le prix PLEIN (`data-full-unit`) — celui que Shopify va
           facturer. On prédit le bon prix au lieu de prolonger l'ancien.
           ⚠️ RESTE APPROXIMATIF quand le palier TIENT : la part de la ligne
             change avec la quantité, et on garde l'unité d'avant pendant ~1 s.
             L'écart est petit et va vers le BAS — jamais la hausse surprise
             ci-dessus. Le corriger demande le modèle d'allocation Shopify, qui
             n'est pas dérivable d'ici.
             ⚠️ CE COMMENTAIRE DISAIT « MONTANT FIXE (onixtoast −24 €, onixgift
               −34 €) » — plus vrai depuis le 03/09 : les cinq paliers sont des
               POURCENTAGES côté Shopify. L'approximation demeure, sa cause a
               changé : ce n'est plus la répartition d'un montant fixe entre les
               lignes, c'est le taux appliqué à un sous-total qui bouge. */
        const uRemise = Number(priceEl.dataset.unitPrice);
        const uPlein = Number(priceEl.dataset.fullUnit) || uRemise;
        const perdLaRemise = uPlein > uRemise && remisePerdueApres(article, oldQty, newQty);
        const uApres = perdLaRemise ? uPlein : uRemise;
        animatePrice(priceEl, uRemise * oldQty, uApres * newQty, 300);
        if (perdLaRemise) {
          priceEl.dataset.unitPrice = String(uPlein);
          passerLignesAuPrixPlein(article);
        }
      }
      // animate compare price if present
      const compareEl = article.querySelector('.onix-cart-item__compare');
      if (compareEl && compareEl.dataset.unitCompare) {
        const uc = Number(compareEl.dataset.unitCompare);
        animatePrice(compareEl, uc * oldQty, uc * newQty, 300);
      }
      // update −/+ data attributes (first btn = minus, second = plus)
      const btns = article.querySelectorAll('[data-cart-quantity]');
      if (btns[0]) btns[0].dataset.quantity = String(Math.max(0, newQty - 1));
      if (btns[1]) btns[1].dataset.quantity = String(newQty + 1);
    };

    const optimisticSubtotal = (delta, compareDelta, countDelta) => {
      if (!subtotalNode) return;
      const amountEl = subtotalNode.querySelector('[data-subtotal-amount]');
      let compareEl = subtotalNode.querySelector('[data-compare-amount]');
      const cd = compareDelta !== undefined ? compareDelta : delta;

      /* Toujours : suit le sous-total HORS remise + le compteur non-bundle.
         Sert au recalcul optimiste de la remise ET à la jauge optimiste. */
      __lastValues.itemsSubtotal = (__lastValues.itemsSubtotal || 0) + delta;
      __lastValues.count = Math.max(0, (__lastValues.count || 0) + (countDelta || 0));

      /* ── Chemin REMISE OPTIMISTE ──────────────────────────────────────
         Un code de palier est ACTIF et pas de bundle → on recalcule le TOTAL
         REMISÉ côté client (sous-total hors remise − remise du palier) au lieu
         d'ajouter juste le delta prix. Le total remisé devient instantané. */
      if (_appliedTier && !_cartHasBundle && amountEl) {
        const valid = __lastValues.count >= _appliedTier.qty;
        const discount = valid ? tierDiscountCents(_appliedTier, __lastValues.itemsSubtotal) : 0;
        const newTotal = Math.max(0, __lastValues.itemsSubtotal - discount);
        let newCompare = (__lastValues.compare || 0) + cd;
        if (newCompare < __lastValues.itemsSubtotal) newCompare = __lastValues.itemsSubtotal;
        animateCount(amountEl, newTotal, 250, 'subtotal');
        updateKlarnaAmount(newTotal, 250);
        majTotalBouton(newTotal, 250, newCompare);
        majUnite(newTotal);
        majBoutonPaiement();
        if (compareEl) animateCount(compareEl, newCompare, 250, 'compare');
        /* Ligne « 🏷 CODE : -X€ » optimiste : maj du montant / masquage si le
           palier n'est plus atteint. Réconciliée au render serveur suivant. */
        const rows = subtotalNode.querySelectorAll('[data-cart-discount-row]');
        if (!valid || discount <= 0) {
          rows.forEach(r => { r.style.display = 'none'; });
        } else {
          rows.forEach((r, idx) => {
            r.style.display = '';
            /* libellé générique, identique au rendu serveur — afficher le CODE
               ici le faisait clignoter (code pendant l'écriture, « Promo »
               après réconciliation) */
            /* ⚠️ Sur le chemin optimiste, si la remise vient d'un PALIER on
               masque la ligne au lieu de la remplir : D4 la porte sur
               l'échelle. Sans ça elle réapparaîtrait une fraction de seconde
               à chaque changement de quantité. */
            if (_appliedTier) { r.style.display = 'none'; }
            else if (idx === 0) {
              r.style.display = '';
              r.innerHTML = '<i>✓</i>' + escapeHtml(t.promoLabel || 'Promo')
                + ' <b>-' + sansCentimesNuls(formatMoney(discount)) + '</b>';
            }
          });
        }
      } else {
        /* ── Fallback delta-based (aucun code de palier actif) ── */
        /* ⚠️ CALCULÉ AVANT L'APPEL AU BOUTON, pas après : posé plus bas, le
           bouton recevait la valeur du tour précédent et le prix barré avait
           un clic de retard sur le total. */
        const newCompare = (__lastValues.compare || 0) + cd;
        if (amountEl) {
          const newTotal = (__lastValues.subtotal || 0) + delta;
          animateCount(amountEl, newTotal, 250, 'subtotal');
          updateKlarnaAmount(newTotal, 250);
          majTotalBouton(newTotal, 250, newCompare);
          majUnite(newTotal);
          majBoutonPaiement();
        }
        // Create compare span if it doesn't exist yet but we have a compare delta
        if (!compareEl && cd !== delta && newCompare > ((__lastValues.subtotal || 0) + delta)) {
          const span = document.createElement('span');
          span.setAttribute('data-compare-amount', '');
          span.style.cssText = 'text-decoration:line-through;color:#ffffff;font-size:0.82em;font-weight:400;margin-left:0.4rem;';
          if (amountEl) amountEl.after(span);
          compareEl = span;
        }
        if (compareEl) {
          animateCount(compareEl, newCompare, 250, 'compare');
        }
      }

      /* Jauge de progression OPTIMISTE (instantanée), hors bundle : on pousse
         le nouveau compteur + sous-total non-bundle directement dans la jauge
         sans attendre le re-fetch serveur. */
      if (window.__onixPromoGauge && !_cartHasBundle) {
        try { window.__onixPromoGauge(__lastValues.count, __lastValues.itemsSubtotal); } catch (_) {}
      }
    };

    const optimisticRemove = (article) => {
      article.style.transition = 'opacity 0.18s ease, max-height 0.25s ease 0.05s, padding 0.25s ease 0.05s, margin 0.25s ease 0.05s';
      article.style.overflow = 'hidden';
      article.style.maxHeight = article.offsetHeight + 'px';
      article.dataset.removing = '1';
      requestAnimationFrame(() => {
        article.style.opacity = '0';
        article.style.maxHeight = '0';
        article.style.padding = '0';
        article.style.margin = '0';
      });
    };

    /* L'INVERSE de optimisticRemove — il n'en avait aucun, et c'est ce qui a
       coûté le plus cher au banc du 23/08 : requêtes /cart/* volontairement
       jamais résolues (réseau mobile qui tombe), la ligne restait à 6 s un
       FANTÔME de 2 px de haut. Disparue de l'écran, toujours dans le panier
       payé au paiement. Rien ne la ramenait, parce que le seul chemin de
       retour était un re-rendu que la requête perdue ne déclenchait jamais.
       On remet l'article dans son état de départ : c'est le rendu serveur
       suivant qui tranchera s'il doit vraiment partir. */
    const annulerRetraitOptimiste = (article) => {
      delete article.dataset.removing;
      article.style.transition = '';
      article.style.overflow = '';
      article.style.maxHeight = '';
      article.style.opacity = '';
      article.style.padding = '';
      article.style.margin = '';
    };

    /* Empty-state fast path: if the remove that just started leaves the
       drawer body with zero visible items, inject the empty state immediately
       after the collapse animation so the user doesn't stare at a blank
       drawer while waiting for /cart/update.js + /cart.js to round-trip. */
    const optimisticEmptyIfLast = () => {
      if (!drawerBody) return;
      const liveItems = drawerBody.querySelectorAll(
        '.onix-cart-item:not([data-removing]), .onix-bundle-card:not([data-removing])'
      );
      if (liveItems.length > 0) return;
      setTimeout(() => {
        if (!drawerBody) return;
        const stillLive = drawerBody.querySelectorAll(
          '.onix-cart-item:not([data-removing]), .onix-bundle-card:not([data-removing])'
        );
        if (stillLive.length > 0) return;
        if (drawerBody.querySelector('.onix-cart-drawer__empty')) return;
        drawerBody.innerHTML = `
        <div class="onix-cart-drawer__empty"></div>`;
        if (window.__onixRenderEmptyExtras) window.__onixRenderEmptyExtras(drawerBody);
      }, 260);
    };

    /* ── Debounced changeLine: accumulates rapid clicks, sends only final qty ── */
    const _pendingChanges = {};
    const _changeTimers = {};
    /* Bundle merged-card pending state: per variant, tracks the real server
       baseline qty + the optimistic target qty so spam clicks only fire a
       single net add/change once the user stops. */
    const _bundlePending = {};
    /* Pending batch removals: any X-click (bundle or not) adds its line key(s)
       here, and a short debounce flushes them all in one /cart/update.js so
       rapid deletes never race on stale line keys. */
    const _pendingRemoveKeys = new Set();
    let _removeTimer = null;
    const DEBOUNCE_MS = 350;
    const REMOVE_DEBOUNCE_MS = 80;
    /* Serialize writes so parallel clicks (remove all items) never race. */
    let _writeChain = Promise.resolve();

    /* Clear every pending timer/state when the page is unloaded so nothing
       fires on a torn-down DOM (memory leaks + "Cannot set properties of null"
       errors on page navigation). */
    const _cleanupTimers = () => {
      Object.values(_changeTimers).forEach(t => { try { clearTimeout(t); } catch (e) {} });
      Object.keys(_changeTimers).forEach(k => delete _changeTimers[k]);
      Object.values(_bundlePending).forEach(p => { if (p && p.timer) { try { clearTimeout(p.timer); } catch (e) {} } });
      Object.keys(_bundlePending).forEach(k => delete _bundlePending[k]);
      if (_removeTimer) { try { clearTimeout(_removeTimer); } catch (e) {} _removeTimer = null; }
      _pendingRemoveKeys.clear();
    };
    window.addEventListener('pagehide', _cleanupTimers, { once: true });

    /* Single source of truth for "are we busy?" — reads across the three
       pending buckets (line changes, bundle changes, removals). Used to
       decide when it's safe to render / refresh, and to toggle the visual
       loading state on quantity buttons. */
    const hasPendingWrites = () =>
      Object.keys(_pendingChanges).length > 0 ||
      Object.keys(_bundlePending).length > 0 ||
      _pendingRemoveKeys.size > 0;

    /* Writing indicator: cheap class toggle on the root so CSS can grey
       out +/- buttons as soon as a write is queued (even during the debounce
       window), and remove it once the buckets drain AND any in-flight fetches
       settle. We recompute the class from authoritative state rather than
       manual ref-counts so it can't drift on/off.

       The shimmer bar (CSS ::before of the panel) is a determinate progress
       bar driven by NProgress-style logic: when writing starts, it grows
       from 0% toward 90% with ease-out. When writing ends, we snap it to
       100% then fade out. That way one full traversal == one cart write,
       no infinite loop "for nothing". */
    let _inFlightWrites = 0;
    let _doneTimer = null;

    /* ═══ LE FILET DE L'ÉTAT « EN ÉCRITURE » ══════════════════════════════
       `endWriting()` ne vit que dans un `finally` : une requête qui ne revient
       JAMAIS ne l'atteint pas. Or `loggedFetch` n'a aucun délai maximal — une
       promesse jamais résolue reste en attente indéfiniment.
       Mesuré le 23/08 au banc, /cart/* volontairement jamais résolus :
         à 1 s, 3 s, 6 s ET 10 s → le tiroir portait toujours `--writing`,
         les +/− étaient toujours à 0,55 d'opacité, la barre cyan pulsait
         toujours, et une ligne supprimée restait en fantôme de 2 px.
       Le tiroir ne redevenait jamais utilisable. Ce filet est le SEUL chemin
       de retrait qui ne dépende pas du réseau : au bout de 7 s on relâche
       l'affichage, on remet les lignes fantômes, et on relit le panier pour
       repartir de la vérité serveur. Le compteur `_inFlightWrites` a un
       plancher à 0, donc une réponse qui arrive après coup ne casse rien. */
    const FILET_ECRITURE_MS = 7000;
    let _filetEcriture = null;
    const libererEcriture = () => {
      _filetEcriture = null;
      if (!hasPendingWrites() && _inFlightWrites === 0) return;  /* déjà rentré dans l'ordre */
      _inFlightWrites = 0;
      /* Les seaux d'attente aussi : les laisser pleins ferait revenir la classe
         au tour suivant, et on retomberait dans le même blocage.
         ⚠️ `_cleanupTimers()` vide les MINUTEURS, pas `_pendingChanges`. Une
           quantité mise en file juste avant que le filet parte y resterait, donc
           `hasPendingWrites()` répondrait « oui » pour toujours : la classe
           reviendrait, le filet se réarmerait, et on tournerait en rond toutes
           les 7 s. Le seau se vide donc ici, explicitement. */
      _cleanupTimers();
      Object.keys(_pendingChanges).forEach((k) => delete _pendingChanges[k]);
      drawerRoot
        .querySelectorAll('.onix-cart-item[data-removing], .onix-bundle-card[data-removing]')
        .forEach(annulerRetraitOptimiste);
      syncWritingClass();
      /* Relecture, pas ré-écriture : ce qui est affiché redevient ce que le
         serveur a vraiment. Si le réseau est toujours coupé, elle échoue sans
         reposer de verrou — elle ne passe pas par beginWriting(). */
      safeRefreshCart();
    };

    const syncWritingClass = () => {
      const writing = hasPendingWrites() || _inFlightWrites > 0;
      const wasWriting = drawerRoot.classList.contains('onix-cart-drawer--writing');
      if (writing) {
        if (_doneTimer) { clearTimeout(_doneTimer); _doneTimer = null; }
        drawerRoot.classList.remove('onix-cart-drawer--writing-done');
        drawerRoot.classList.add('onix-cart-drawer--writing');
        if (!_filetEcriture) _filetEcriture = setTimeout(libererEcriture, FILET_ECRITURE_MS);
      } else {
        if (_filetEcriture) { clearTimeout(_filetEcriture); _filetEcriture = null; }
        if (wasWriting) {
          /* End of write: switch to "done" state so CSS snaps the bar to 100%
             and fades out smoothly. Then remove both classes after the fadeout
             completes (matches the CSS transition duration). */
          drawerRoot.classList.add('onix-cart-drawer--writing-done');
          drawerRoot.classList.remove('onix-cart-drawer--writing');
          if (_doneTimer) clearTimeout(_doneTimer);
          _doneTimer = setTimeout(() => {
            drawerRoot.classList.remove('onix-cart-drawer--writing-done');
            _doneTimer = null;
          }, 500);
        }
      }
    };
    const beginWriting = () => { _inFlightWrites++; syncWritingClass(); };
    const endWriting = () => { _inFlightWrites = Math.max(0, _inFlightWrites - 1); syncWritingClass(); };

    const flushChange = (key) => {
      const qty = _pendingChanges[key];
      delete _pendingChanges[key];
      delete _changeTimers[key];
      beginWriting();
      _writeChain = _writeChain.then(async () => {
        try {
          /* La qty change part directement : l'application des codes (bundle
             + paliers) est centralisée dans cart-drawer-promo-suggest.js, qui
             attend que le panier soit STABLE (settling) avant de poser un code.
             Aucune coordination de code à attendre ici — le settling garantit
             une transition de code propre, sans cascade. */
          const res = await loggedFetch(`${rootPath}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: key, quantity: qty }),
          });
          const cart = await res.json();
          if (!hasPendingWrites()) renderCart(cart);
        } catch { safeRefreshCart(); }
        finally { endWriting(); }
      });
      return _writeChain;
    };

    const changeLine = (key, quantity) => {
      _pendingChanges[key] = quantity;
      clearTimeout(_changeTimers[key]);
      syncWritingClass();
      if (quantity === 0) {
        // Remove: fire immediately, no debounce
        flushChange(key);
      } else {
        _changeTimers[key] = setTimeout(() => flushChange(key), DEBOUNCE_MS);
      }
    };

    /* ── Debounced bundle quantity: accumulates spam clicks on merged
       bundle cards and fires a single net add/change when the user stops. */
    const flushBundleChange = (variantId) => {
      const pending = _bundlePending[variantId];
      if (!pending) return;
      delete _bundlePending[variantId];
      const { baseline, target, bundleKeys } = pending;
      const delta = target - baseline;
      beginWriting();
      _writeChain = _writeChain.then(async () => {
        try {
          if (delta > 0) {
            /* Use change.js on the FIRST existing bundle line so we
               preserve its full properties hash (including
               _bundle_session from the offer popup). Calling add.js with
               only _offer_bundle would spawn a fresh line because the
               property set differs, which would split the bundle visually
               and break subsequent decrement math. */
            if (bundleKeys && bundleKeys.length > 0) {
              const entry = bundleKeys[0];
              await loggedFetch(`${rootPath}cart/change.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: entry.key, quantity: entry.qty + delta }),
              });
            } else {
              await loggedFetch(`${rootPath}cart/add.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: Number(variantId), quantity: delta, properties: { _offer_bundle: 'true' } }] }),
              });
            }
          } else if (delta < 0) {
            let toRemove = -delta;
            for (let i = bundleKeys.length - 1; i >= 0 && toRemove > 0; i--) {
              const entry = bundleKeys[i];
              const peel = Math.min(entry.qty, toRemove);
              const newTarget = entry.qty - peel;
              await loggedFetch(`${rootPath}cart/change.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: entry.key, quantity: newTarget }),
              });
              toRemove -= peel;
            }
          }
          if (!hasPendingWrites()) await safeRefreshCart();
        } catch { safeRefreshCart(); }
        finally { endWriting(); }
      });
    };

    const flushRemoves = () => {
      if (_pendingRemoveKeys.size === 0) return;
      const keys = [...(_pendingRemoveKeys)];
      _pendingRemoveKeys.clear();
      _removeTimer = null;
      beginWriting();
      _writeChain = _writeChain.then(async () => {
        try {
          const updates = {};
          keys.forEach(k => { updates[k] = 0; });
          /* /cart/update.js returns the full updated cart — reuse it directly
             instead of firing a second /cart.js refresh round-trip. Saves
             ~250-450ms per removal (matches flushChange's behaviour). */
          const res = await loggedFetch(`${rootPath}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
          const cart = await res.json();
          if (!hasPendingWrites()) await safeRefreshCart(cart);
        } catch { safeRefreshCart(); }
        finally { endWriting(); }
      });
    };

    const queueRemove = (keys) => {
      keys.forEach(k => _pendingRemoveKeys.add(k));
      clearTimeout(_removeTimer);
      syncWritingClass();
      _removeTimer = setTimeout(flushRemoves, REMOVE_DEBOUNCE_MS);
    };

    const queueBundleChange = (variantId, newTarget, baselineQty, bundleKeys) => {
      if (!_bundlePending[variantId]) {
        _bundlePending[variantId] = { baseline: baselineQty, target: newTarget, bundleKeys, timer: null };
      } else {
        _bundlePending[variantId].target = newTarget;
        _bundlePending[variantId].bundleKeys = bundleKeys;
      }
      clearTimeout(_bundlePending[variantId].timer);
      syncWritingClass();
      if (newTarget === 0) {
        flushBundleChange(variantId);
      } else {
        _bundlePending[variantId].timer = setTimeout(() => flushBundleChange(variantId), DEBOUNCE_MS);
      }
    };

    cartToggleButtons.forEach((button) => {
      button.addEventListener("click", async () => {
        openDrawer();
        await safeRefreshCart();
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener("click", closeDrawer);
    });

    drawerRoot.addEventListener("click", (event) => {
      const quantityButton = event.target.closest("[data-cart-quantity]");
      const removeButton = event.target.closest("[data-cart-remove]");
      const closeButton = event.target.closest("[data-cart-close]");
      const checkoutLink = event.target.closest('.onix-cart-drawer__checkout');
      /* Le CSS neutralise déjà les clics souris ; au clavier, Entrée suit le lien
         malgré `pointer-events: none`. Ce garde ferme le dernier chemin — et il
         vaut maintenant pour les DEUX états qui interdisent le départ : panier
         vide (`aria-disabled`) et départ déjà lancé (`aria-busy`). */
      if (checkoutLink) {
        if (checkoutLink.getAttribute('aria-disabled') === 'true'
            || checkoutLink.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          return;
        }
        /* Le retour se pose SUR LE CLIC, pas au retour du serveur : le lien
           part vers /checkout et plus rien ne se passera dans cette page.
           Mesuré muet pendant 2 500 ms avant ce correctif. */
        poserPaiementEnCours(checkoutLink);
        return;
      }
      if (closeButton) { closeDrawer(); return; }

      /* Bundle discount is now applied server-side (session). Checkout link
         goes straight to /checkout and Shopify carries the discount through. */

      if (quantityButton) {
        const key = quantityButton.dataset.lineKey;
        const article = quantityButton.closest('.onix-cart-item');
        /* Derive newQty from the live qtySpan + direction so the value
           is always relative to the CURRENT optimistic state. Reading
           dataset.quantity here is unsafe: a renderCart landing between
           the previous click and this one would reset data-quantity to
           the server baseline, making the next click jump backwards. */
        const _dir = Number(quantityButton.dataset.cartDirection);
        const _qtySpan = article && article.querySelector('.onix-cart-item__quantity > span');
        const _currentQty = _qtySpan ? Number(_qtySpan.textContent) : Number(quantityButton.dataset.quantity);
        const newQty = _dir ? Math.max(0, _currentQty + _dir) : Number(quantityButton.dataset.quantity);
        /* No-op guard: if the computed newQty equals what's already shown,
           skip the whole pipeline. Prevents "phantom" clicks from firing
           duplicate writes when the button is spam-hammered at qty=1 (minus)
           or right after a render resets the counter. */
        if (newQty === _currentQty) return;
        const bundleKeysAttr = article && article.dataset.bundleKeys;
        if (bundleKeysAttr) {
          /* Merged bundle card: optimistic UI + debounced server sync so
             spam clicks fire only one net add/change. */
          let bundleKeys;
          try { bundleKeys = JSON.parse(bundleKeysAttr); } catch (_) { return; }
          const variantId = article.dataset.bundleVariant;
          const qtySpan = article.querySelector('.onix-cart-item__quantity > span');
          const oldQty = qtySpan ? Number(qtySpan.textContent) : 1;
          const priceEl = article.querySelector('.onix-cart-item__price');
          const compareEl = article.querySelector('.onix-cart-item__compare');
          const unitCompare = compareEl && compareEl.dataset.unitCompare ? Number(compareEl.dataset.unitCompare) : 0;
          const bundleDelta = (priceEl && Number(priceEl.dataset.fullUnit)) || BUNDLE_EXTRA_FALLBACK_CENTS;
          const priceDelta = (newQty - oldQty) * bundleDelta;
          const compareDelta = (newQty - oldQty) * unitCompare;
          if (newQty === 0) {
            optimisticSubtotal(priceDelta, compareDelta, -oldQty);
            optimisticRemove(article);
            cartCountNodes.forEach(n => { n.textContent = Math.max(0, Number(n.textContent) - oldQty); });
          } else {
            optimisticQuantity(article, newQty, oldQty);
            optimisticSubtotal(priceDelta, compareDelta, newQty - oldQty);
            cartCountNodes.forEach(n => { n.textContent = Number(n.textContent) + (newQty - oldQty); });
          }
          /* Baseline for this variant is the qty from the last server render
             (i.e. sum of all underlying bundleKeys). First click captures it;
             subsequent clicks only update `target`. */
          const baselineQty = _bundlePending[variantId]
            ? _bundlePending[variantId].baseline
            : bundleKeys.reduce((s, e) => s + e.qty, 0);
          queueBundleChange(variantId, newQty, baselineQty, bundleKeys);
          return;
        }
        if (article) {
          const priceEl = article.querySelector('.onix-cart-item__price');
          const compareEl = article.querySelector('.onix-cart-item__compare');
          const qtySpan = article.querySelector('.onix-cart-item__quantity > span');
          const oldQty = qtySpan ? Number(qtySpan.textContent) : 1;
          const isBundleLine = !!(priceEl && priceEl.dataset.bundle === '1');
          const unitCents = priceEl && priceEl.dataset.unitPrice ? Number(priceEl.dataset.unitPrice) : 0;
          const unitCompare = compareEl && compareEl.dataset.unitCompare ? Number(compareEl.dataset.unitCompare) : unitCents;

          /* Bundle lines: optimistic delta = full variant price per extra
             unit (read dynamically from data-full-unit). */
          let priceDelta, compareDelta;
          if (isBundleLine) {
            const bundleDelta = (priceEl && Number(priceEl.dataset.fullUnit)) || BUNDLE_EXTRA_FALLBACK_CENTS;
            priceDelta = (newQty - oldQty) * bundleDelta;
            compareDelta = (newQty - oldQty) * unitCompare;
          } else {
            priceDelta = (newQty - oldQty) * unitCents;
            compareDelta = (newQty - oldQty) * unitCompare;
          }

          if (newQty === 0) {
            optimisticSubtotal(priceDelta, compareDelta, -oldQty);
            optimisticRemove(article);
            cartCountNodes.forEach(n => { n.textContent = Math.max(0, Number(n.textContent) - oldQty); });
          } else {
            optimisticQuantity(article, newQty, oldQty);
            optimisticSubtotal(priceDelta, compareDelta, newQty - oldQty);
            cartCountNodes.forEach(n => { n.textContent = Number(n.textContent) + (newQty - oldQty); });
          }
        }
        changeLine(key, newQty);
        return;
      }

      if (removeButton) {
        const key = removeButton.dataset.lineKey;
        /* Match both the standard cart item and the locked bundle-card item
           so ✕ clicks inside the "OFFRE DÉCOUVERTE" card are captured too. */
        const article = removeButton.closest('.onix-cart-item, .onix-bundle-card__item');
        const bundleKeysAttr = article && article.dataset.bundleKeys;
        if (bundleKeysAttr) {
          let bundleKeys;
          try { bundleKeys = JSON.parse(bundleKeysAttr); } catch (_) { return; }
          const qtySpan = article.querySelector('.onix-cart-item__quantity > span, .onix-bundle-card__qty span');
          const oldQty = qtySpan ? Number(qtySpan.textContent) : 1;
          const priceEl = article.querySelector('.onix-cart-item__price, .onix-bundle-card__price');
          const compareEl = article.querySelector('.onix-cart-item__compare, .onix-bundle-card__compare');
          const unitCompare = compareEl && compareEl.dataset.unitCompare ? Number(compareEl.dataset.unitCompare) : 0;
          const priceDelta = priceEl && priceEl.dataset.lineTotal ? -Number(priceEl.dataset.lineTotal) : 0;
          /* Race-fix: cancel any pending debounced writes for the underlying
             line keys AND for the merged bundle variant, otherwise a late
             change.js/add.js resurrects the line right after update.js zeroes it. */
          bundleKeys.forEach(e => {
            delete _pendingChanges[e.key];
            if (_changeTimers[e.key]) { clearTimeout(_changeTimers[e.key]); delete _changeTimers[e.key]; }
          });
          const variantId = article.dataset.bundleVariant;
          if (variantId && _bundlePending[variantId]) {
            clearTimeout(_bundlePending[variantId].timer);
            delete _bundlePending[variantId];
          }

          /* Bundle remove fast path: if this click will orphan
             the sibling bundle item (exactly 2 visible bundle items), collapse
             the 5-step strip flow (update → fetch → change → add → fetch →
             clearDiscount → fetch → render) into a single round-trip pair:
               1× update.js  (zero clicked + orphan keys in one call)
               1× add.js     (re-add orphan with clean props)
               1× refresh    (final reconciliation render)
             clearSessionDiscount is fired-and-forgotten in parallel. */
          const bundleArticles = drawerRoot.querySelectorAll('.onix-bundle-card__item');
          const siblingArticle = bundleArticles.length === 2
            ? Array.from(bundleArticles).find(a => a !== article)
            : null;

          if (siblingArticle) {
            const siblingKeysAttr = siblingArticle.dataset.bundleKeys;
            const siblingVariantId = siblingArticle.dataset.bundleVariant;
            if (siblingKeysAttr && siblingVariantId) {
              let siblingKeys = [];
              try { siblingKeys = JSON.parse(siblingKeysAttr); } catch (e) { siblingKeys = []; }
              const siblingQtySpan = siblingArticle.querySelector('.onix-bundle-card__qty span');
              const siblingQty = siblingQtySpan ? Number(siblingQtySpan.textContent) : 1;
              const siblingPriceEl = siblingArticle.querySelector('.onix-bundle-card__price');
              const siblingBundleTotal = siblingPriceEl && siblingPriceEl.dataset.lineTotal
                ? Number(siblingPriceEl.dataset.lineTotal) : 0;
              const siblingFullUnit = siblingPriceEl && siblingPriceEl.dataset.fullUnit
                ? Number(siblingPriceEl.dataset.fullUnit) : 0;
              /* Orphan reverts to its full unit price × qty (bundle discount gone). */
              const siblingNewTotal = siblingFullUnit * siblingQty;
              const siblingPriceDelta = siblingNewTotal - siblingBundleTotal;

              /* Optimistic UI: remove the whole bundle card + animate subtotal.
                 The refresh that follows will replace the container with the
                 normal regular-item row, built from server truth. */
              const bundleCardEl = article.closest('.onix-bundle-card');
              optimisticSubtotal(priceDelta + siblingPriceDelta, -(unitCompare * oldQty));
              cartCountNodes.forEach(n => { n.textContent = Math.max(0, Number(n.textContent) - oldQty); });
              if (bundleCardEl) optimisticRemove(bundleCardEl); else optimisticRemove(article);

              /* Reset popup state + clear cached discount immediately so the
                 offer UI reopens cleanly next time. */
              if (window.__onixResetOfferBundle) window.__onixResetOfferBundle();
              delete window.__onixOfferDiscountCode;

              const zeroUpdates = {};
              bundleKeys.forEach(e => { zeroUpdates[e.key] = 0; });
              siblingKeys.forEach(e => { zeroUpdates[e.key] = 0; });

              beginWriting();
              _writeChain = _writeChain.then(async () => {
                try {
                  /* 1) Zero both lines in a single update.js */
                  await loggedFetch(`${rootPath}cart/update.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ updates: zeroUpdates }),
                  });
                  /* 2) Re-add the orphan with clean (non-bundle) props */
                  await loggedFetch(`${rootPath}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ items: [{ id: Number(siblingVariantId), quantity: siblingQty }] }),
                  });
                  /* 3) Fire-and-forget discount clear (non-blocking) */
                  clearSessionDiscount().catch(() => {});
                  /* 4) Single final reconciliation render */
                  await safeRefreshCart();
                } catch (e) {
                  safeRefreshCart();
                } finally {
                  endWriting();
                }
              });
              return;
            }
          }

          /* Fallback: standard debounced remove flow (handles >2 bundle items
             or missing sibling data). */
          optimisticSubtotal(priceDelta, -(unitCompare * oldQty), -oldQty);
          optimisticRemove(article);
          cartCountNodes.forEach(n => { n.textContent = Math.max(0, Number(n.textContent) - oldQty); });
          optimisticEmptyIfLast();
          queueRemove(bundleKeys.map(e => e.key));
          return;
        }
        if (article) {
          const priceEl = article.querySelector('.onix-cart-item__price');
          const compareEl = article.querySelector('.onix-cart-item__compare');
          const qtySpan = article.querySelector('.onix-cart-item__quantity > span');
          const oldQty = qtySpan ? Number(qtySpan.textContent) : 1;
          const isBundleLine = !!(priceEl && priceEl.dataset.lineTotal);
          const unitCents = priceEl && priceEl.dataset.unitPrice ? Number(priceEl.dataset.unitPrice) : 0;
          const unitCompare = compareEl && compareEl.dataset.unitCompare ? Number(compareEl.dataset.unitCompare) : unitCents;
          const priceDelta = isBundleLine
            ? -Number(priceEl.dataset.lineTotal)
            : -(unitCents * oldQty);
          optimisticSubtotal(priceDelta, -(unitCompare * oldQty), -oldQty);
          optimisticRemove(article);
          cartCountNodes.forEach(n => { n.textContent = Math.max(0, Number(n.textContent) - oldQty); });
          optimisticEmptyIfLast();
        }
        /* Race-fix: same as bundle path — purge pending qty debounce for
           this key so a stale change.js can't resurrect the line. */
        delete _pendingChanges[key];
        if (_changeTimers[key]) { clearTimeout(_changeTimers[key]); delete _changeTimers[key]; }
        queueRemove([key]);
        return;
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeDrawer();
    });

    /* Build a single cart-item HTML from an item object — MÊME design que
       renderCart (titre découpé club/sous-titre, pastille de taille SUR la
       photo, corbeille dans la bottomline) : la carte optimiste d'un ajout
       ne doit pas apparaître dans l'ancien design puis « sauter » vers le
       nouveau au re-rendu serveur. */
    const buildItemHtml = (item) => {
      const capMap = window.__onixCompareAtPrices || {};
      const cap = capMap[item.variant_id];
      const unitPrice = item.final_price;
      const lineTotal = unitPrice * item.quantity;
      const effectiveCompare = cap && cap > unitPrice ? cap : (item.original_price && item.original_price > unitPrice ? item.original_price : 0);
      const comparePrice = effectiveCompare
        ? `<span class="onix-cart-item__compare" data-unit-compare="${effectiveCompare}">${formatMoney(effectiveCompare * item.quantity)}</span>`
        : "";
      const sizeValue = item.variant_title || '';
      const pastilleTaille = sizeValue ? `<span class="onix-cart-item__size-pill">${escapeHtml(sizeValue)}</span>` : '';
      const imgSrc = item.featured_image ? (item.featured_image.url || item.image || '') : (item.image || '');
      const media = imgSrc
        ? `<a class="onix-cart-item__media" href="${escapeHtml(item.url)}"><img class="onix-cart-item__image" src="${escapeHtml(imgSrc)}" alt="${escapeHtml(item.product_title || item.title)}">${pastilleTaille}</a>`
        : `<div class="onix-cart-item__media">${pastilleTaille}</div>`;
      const nextMinus = Math.max(0, item.quantity - 1);
      const props = item.properties && typeof item.properties === 'object'
        ? Object.entries(item.properties).filter(([k,v]) => !k.startsWith('_') && v !== '').map(([k,v]) => `<li><span class="onix-cart-item__prop-key">${escapeHtml(k)} :</span> ${escapeHtml(v)}</li>`).join('')
        : '';
      const d = titreEnDeux(item.product_title || item.title);
      const titre = `<span class="onix-cart-item__club">${escapeHtml(d.club)}</span>` + (d.reste ? `<span class="onix-cart-item__sous">${escapeHtml(d.reste)}</span>` : '');
      return `
        <article class="onix-cart-item" data-cart-item-key="${escapeHtml(item.key)}" data-variant-id="${escapeHtml(String(item.variant_id))}" data-qte="${item.quantity}">
          ${media}
          <div class="onix-cart-item__content">
            <div class="onix-cart-item__topline">
              <h3 class="onix-cart-item__title"><a href="${escapeHtml(item.url)}">${titre}</a></h3>
            </div>
            ${props ? `<ul class="onix-cart-item__props onix-cart-item__props--brut">${props}</ul>` : ''}
            <div class="onix-cart-item__bottomline">
              <div class="onix-cart-item__quantity" aria-label="${escapeHtml(t.quantity)}">
                <button type="button" class="onix-cart-item__moins" data-cart-quantity data-cart-direction="-1" data-line-key="${escapeHtml(item.key)}" data-quantity="${nextMinus}" aria-label="${escapeHtml(item.quantity === 1 ? t.remove : t.quantityDown)}"><span class="onix-cart-item__moins-signe" aria-hidden="true">-</span><svg class="onix-cart-item__moins-corbeille" aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg></button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-quantity data-cart-direction="1" data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}">+</button>
              </div>
              <div class="onix-cart-item__pricing">
                <span class="onix-cart-item__price" data-unit-price="${unitPrice}" data-line-total="${lineTotal}" data-full-unit="${unitPrice}">${formatMoney(lineTotal)}</span>
                ${comparePrice}
              </div>
              <button class="onix-cart-item__remove" type="button" data-cart-remove data-line-key="${escapeHtml(item.key)}" aria-label="${escapeHtml(t.remove)}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg></button>
            </div>
          </div>
        </article>`;
    };

    /* Scrape product info from the page DOM for instant optimistic add */
    const scrapeProductFromPage = (form) => {
      const section = form.closest('.onix-product, .shopify-section, [data-section-type]') || document;
      // Title — combine main title + subtitles (split across elements on product page)
      const titleEl = section.querySelector('.onix-product__title, h1');
      let title = titleEl ? titleEl.textContent.trim() : '';
      const subtitles = section.querySelectorAll('.onix-product__subtitle');
      if (subtitles.length) {
        const parts = [];
        subtitles.forEach(s => { const t = s.textContent.trim(); if (t) parts.push(t); });
        if (parts.length) title += ' - ' + parts.join(' - ');
      }
      // Image — first .onix-product__image (could be <img> or inside a slide)
      const imgEl = section.querySelector('.onix-product__image, .onix-product__slide img, img.onix-product__image');
      const image = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';
      // Price
      const priceEl = section.querySelector('.onix-product__price');
      let priceCents = 0;
      if (priceEl) {
        const raw = priceEl.textContent.replace(/[^\d,.-]/g, '').replace(',', '.');
        priceCents = Math.round(parseFloat(raw) * 100) || 0;
      }
      // Compare-at price (strikethrough)
      const compareEl = section.querySelector('.onix-product__compare-price');
      let compareCents = 0;
      if (compareEl) {
        const rawC = compareEl.textContent.replace(/[^\d,.-]/g, '').replace(',', '.');
        compareCents = Math.round(parseFloat(rawC) * 100) || 0;
      }
      // Selected size — button.is-selected with data-size-value, or select, or hidden input
      const sizeBtn = section.querySelector('[data-size-button].is-selected');
      const sizeSelect = section.querySelector('select[name="id"]');
      let variantTitle = '';
      if (sizeBtn) {
        variantTitle = sizeBtn.getAttribute('data-size-value') || sizeBtn.textContent.trim();
      } else if (sizeSelect) {
        const opt = sizeSelect.options[sizeSelect.selectedIndex];
        if (opt) variantTitle = opt.textContent.trim().split(' - ')[0].trim();
      }
      // Product URL
      const url = window.location.pathname;
      return { title, image, priceCents, compareCents, variantTitle, url };
    };

    document.addEventListener("submit", async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"], .onix-product__form, .onix-sticky-cart__form')) return;

      event.preventDefault();

      /* ⛔ ON NE TOUCHE PLUS AU CONTENU DU BOUTON — 30/08, 08h50. C'EST LUI QUI
           A DONNÉ LA CONDITION QUI ME MANQUAIT : « le bouton ajouter au panier
           il apparaît comme ça QUAND ON CLIQUE DESSUS et que l'animation passe ».
           Il signalait ce défaut depuis la veille au soir ; je ne l'avais jamais
           reproduit parce que je regardais le bouton AVANT le clic, ou longtemps
           après. Il fallait avoir cliqué.

         CE QUI SE PASSAIT, MESURÉ :
           avant le clic ... 2 éléments (libelle + fleche, puis les deux prix)
           après le clic ... 0 élément, du texte brut « au panier 24,99EUR49,99EUR »
         Trois lignes en étaient responsables : on sauvegardait le TEXTE seul, on
         écrasait le contenu par des points de suspension — ce qui DÉTRUIT tous
         les enfants — puis on « restaurait » du texte plat. La structure en deux
         lignes, l'espacement de 0,75 em entre les deux prix et le barré étaient
         perdus DÉFINITIVEMENT, jusqu'au prochain rechargement.

         ⚠️ ET C'EST L'ESPACEMENT QU'IL M'AVAIT FAIT CORRIGER LE 24/08 (les deux
           prix se lisaient comme un seul nombre). Il était bien corrigé — et un
           autre fichier le détruisait au premier clic. Une correction qu'un autre
           fichier annule ne se voit dans aucun des deux.

         ➜ Le bouton porte DÉJÀ un état visuel pendant l'ajout : la classe
           est-en-cours pose une barre de progression animée (onix-product.css).
           Le remplacement de texte faisait doublon ET cassait tout. On ne garde
           que la classe.
         ⚠️ aria-busy remplace le changement de texte pour les lecteurs d'écran :
           changer le libellé d'un bouton en cours d'action le fait relire en
           entier, ce qui est plus perturbant que le silence.
         ⚠️ PLUS DE SAUVEGARDE À RESTAURER, donc plus de double-clic qui
           enregistrerait l'état transitoire comme texte d'origine et figerait le
           bouton dessus pour le reste de la visite. */
      const submitBtn = form.querySelector('[data-product-submit], [type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      const formData = new FormData(form);
      const variantId = formData.get('id') || '';
      const qty = Number(formData.get('quantity')) || 1;

      /* ── Instant optimistic inject from page data ── */
      const pageData = scrapeProductFromPage(form);
      const tempKey = '__optimistic_' + variantId;
      if (drawerBody && pageData.title) {
        const emptyState = drawerBody.querySelector('.onix-cart-drawer__empty');
        if (emptyState) drawerBody.innerHTML = '';

        // Check if variant already in cart (match by variant_id, not price)
        let found = false;
        drawerBody.querySelectorAll('[data-cart-item-key]').forEach(article => {
          if (article.dataset.variantId !== String(variantId)) return;
          const priceEl = article.querySelector('.onix-cart-item__price');
          if (priceEl) {
            const qtySpan = article.querySelector('.onix-cart-item__quantity > span');
            if (qtySpan) {
              const oldQty = Number(qtySpan.textContent);
              optimisticQuantity(article, oldQty + qty, oldQty);
              const cmpDelta = pageData.compareCents > pageData.priceCents ? pageData.compareCents * qty : pageData.priceCents * qty;
              optimisticSubtotal(pageData.priceCents * qty, cmpDelta, qty);
              found = true;
            }
          }
        });

        if (!found) {
          // Pre-populate compare price map so buildItemHtml renders it instantly
          if (pageData.compareCents && pageData.compareCents > pageData.priceCents) {
            if (!window.__onixCompareAtPrices) window.__onixCompareAtPrices = {};
            window.__onixCompareAtPrices[variantId] = pageData.compareCents;
          }
          // Extract flocage properties from form
          const formProps = {};
          for (const [key, val] of formData.entries()) {
            const m = key.match(/^properties\[(.+)\]$/);
            if (m && val) formProps[m[1]] = String(val);
          }
          const optimisticItem = {
            key: tempKey,
            variant_id: variantId,
            product_title: pageData.title,
            title: pageData.title,
            url: pageData.url,
            image: pageData.image,
            featured_image: { url: pageData.image },
            variant_title: pageData.variantTitle,
            quantity: qty,
            final_price: pageData.priceCents,
            properties: formProps,
          };
          drawerBody.insertAdjacentHTML('afterbegin', buildItemHtml(optimisticItem));
          // Defer subtotal animation until drawer is visible
          const _priceDelta = pageData.priceCents * qty;
          const _compareDelta = pageData.compareCents > pageData.priceCents ? pageData.compareCents * qty : pageData.priceCents * qty;
          requestAnimationFrame(() => requestAnimationFrame(() => {
            optimisticSubtotal(_priceDelta, _compareDelta, qty);
          }));
        }

        cartCountNodes.forEach(n => { n.textContent = Number(n.textContent) + qty; });
      }

      openDrawer();

      /* ── Fire API in background ── */
      const addResponse = await loggedFetch(`${rootPath}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      /* on rend simplement le bouton cliquable : son contenu n'a jamais bougé */
      if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }

      if (!addResponse.ok) {
        /* ⛔ PAS `t.error` : c'est le message du CHAMP DE CODE PROMO. Ici c'est
           l'AJOUT qui a échoué, et le client n'a saisi aucun code. Défaut du
           30/08 — voir le commentaire de snippets/cart-drawer.liquid. */
        let message = t.addError;
        try {
          const data = await addResponse.json();
          if (data && data.message) message = data.message;
        } catch (e) {
          console.warn('[onix-cart] failed to parse add.js error response', e);
        }
        closeDrawer();
        showCartError(message);
        // Revert optimistic state
        safeRefreshCart();
        return;
      }

      // Sync with server for accurate data (real keys, discounts, etc.)
      safeRefreshCart();
    });

    document.addEventListener("click", async (event) => {
      const cartLink = event.target.closest('a[href="/cart"], a[href$="/cart"]');
      if (!cartLink) return;

      event.preventDefault();
      setPendingOpen();
      openDrawer();
      await safeRefreshCart();
      clearPendingOpen();
    });

    if (shouldOpenFromStorage || ouvrirDepuisUrl) {
      openDrawer();
      safeRefreshCart().finally(() => {
        clearPendingOpen();
      });
    }

    /* Listen for cart:refresh dispatched by sibling scripts (discount bar
       auto-apply, discount input apply/remove, etc.).

       Two failure modes to avoid:
       1. Refresh DURING the 350 ms debounce window → stale /cart.js (server
          still has OLD qty), renderCart overwrites optimistic UI → "qty
          goes backwards" yo-yo bug.
       2. Refresh skipped entirely → /discount/CODE applied server-side but
          UI never re-fetches → totals stay un-discounted until the next
          user action triggers something else.

       Fix: don't skip — defer. If writes are pending or in flight, poll
       every 100 ms until they all settle, then refresh. _externalRefreshScheduled
       deduplicates rapid-fire dispatches (plusieurs modules peuvent émettre
       cart:refresh sur le même changement de palier). Once everything is committed,
       the server has both the new qty AND the new discount session, so
       safeRefreshCart shows the correct totals.

       Note: the variable is named _externalRefreshScheduled (not
       _refreshPending) to avoid colliding with safeRefreshCart's own
       _refreshPending in the outer scope — duplicate `let` would be a
       SyntaxError that silently breaks the whole script. */
    let _externalRefreshScheduled = false;
    /* ⚠️ DÉDOUBLONNER SANS PERDRE. `_externalRefreshScheduled` seul jetait la
       2ᵉ demande au lieu de la retenir : quand une montée de palier arrivait
       PENDANT l'attente d'un changement de quantité, sa demande de rendu
       partait à la poubelle. Résultat mesuré le 18/08 : `onixtoast` bien posé
       côté serveur (75,96 €), mais la pastille « ✓ Remise 4 maillots »
       ABSENTE du tiroir jusqu'à ce qu'autre chose déclenche un rendu.
       C'est la même famille que le défaut `_selfDispatching` du matin : un
       événement avalé et jamais rejoué. Ici on note qu'il faut recommencer. */
    let _refreshDemandeEncore = false;
    /* Cap the poll so a stuck write never pins the loop forever
       (50 × 100 ms = 5 s total; well beyond any realistic debounce). */
    const MAX_REFRESH_POLL_TICKS = 50;
    document.addEventListener('cart:refresh', (e) => {
      /* ⚠️ ON RECONNAÎT NOS PROPRES ÉVÉNEMENTS À LEUR PAYLOAD, PAS À L'HEURE.
         `renderCart` dispatche toujours avec `detail: { cart }` ; les modules
         externes (barre promo, champ de code, pageshow) dispatchent sans detail.

         L'ancien garde était un drapeau temporel (`_selfDispatching`) levé
         pendant un rAF autour de notre propre dispatch. Il ne distinguait pas
         « mon événement » de « un événement arrivé pendant ma fenêtre » : un
         code promo appliqué juste après un changement de quantité voyait sa
         demande de rafraîchissement JETÉE, et le tiroir restait sur l'ancien
         total indéfiniment. Reproduit le 18/08 : panier 99,96 €, code onix4
         appliqué, panier réel 79,97 €, tiroir figé à 99,96 € à 2, 4, 7 et 10 s.

         Le test sur le payload n'a pas de fenêtre : il est exact. */
      if (e && e.detail && e.detail.cart) return;
      if (_externalRefreshScheduled) { _refreshDemandeEncore = true; return; }
      _externalRefreshScheduled = true;
      let ticks = 0;
      const tryRefresh = () => {
        if ((hasPendingWrites() || _inFlightWrites > 0) && ticks < MAX_REFRESH_POLL_TICKS) {
          ticks++;
          setTimeout(tryRefresh, 100);
          return;
        }
        _externalRefreshScheduled = false;
        safeRefreshCart();
        /* Une demande est arrivée pendant qu'on attendait → on la rejoue une
           fois, après un souffle. Pas de boucle possible : `safeRefreshCart`
           redispatche avec `detail.cart`, et ces événements-là sont ignorés
           par ce même écouteur. */
        if (_refreshDemandeEncore) {
          _refreshDemandeEncore = false;
          setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 150);
        }
      };
      tryRefresh();
    });

    /* ── Mobile bfcache + tab-return resync ────────────────────────────
       Sur mobile (iOS Safari, Android Chrome), quand l'utilisateur navigue
       back, la page est restaurée depuis le back-forward cache SANS
       réexécuter les scripts. Résultat : badges, totaux, bar de promo,
       popup tier figés à l'état pré-navigation pendant que le cart
       serveur a évolué (autre tab, autre device, session expirée).

       On force un re-fetch + render quand :
       - La page revient du bfcache (`pageshow` avec `persisted: true`)
       - La page redevient visible (switch tab, retour d'app switcher)

       Le `safeRefreshCart()` re-render → met à jour `[data-cart-count]`
       → les MutationObservers dans onix-discount-bar et promo-suggest
       fire et mettent toute la chaîne à jour automatiquement. */
    window.addEventListener('pageshow', (e) => {
      /* 2e sortie de l'état « paiement en cours » — et la plus fréquente : le
         client part au paiement, appuie sur « précédent », et le navigateur lui
         rend CETTE page telle quelle depuis le bfcache. Sans ce retrait il
         retrouverait un bouton figé, impossible à recliquer. Hors bfcache aussi
         (rechargement normal), le retrait ne coûte rien. */
      retirerPaiementEnCours();
      if (e.persisted) {
        safeRefreshCart();
        /* On dispatch aussi cart:refresh pour synchroniser explicitement
           les modules qui n'observent pas le badge (auto-apply, etc.). */
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        /* 3e sortie : retour depuis le sélecteur d'applications, ou onglet
           repris. La page n'a pas été rechargée, donc `pageshow` ne part pas. */
        retirerPaiementEnCours();
        safeRefreshCart();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });

    /* ── Public bridge API ──────────────────────────────────────────
       Sibling scripts (cart-drawer-quickadd.js, cart-drawer-discount.js,
       sections/header.liquid offer popup JS) need to trigger drawer
       actions and read drawer state. They reach in via the OnixCart.bridge
       namespace, with legacy window.__onix* aliases kept as shims for
       backwards compatibility with any caller we haven't migrated yet.
       The shims are ONE reference to the same function/value, so there's
       zero runtime cost and no duplication risk. */
    window.OnixCart = window.OnixCart || {};
    window.OnixCart.bridge = Object.freeze({
      openDrawer,
      refreshCart: safeRefreshCart,
      buildItemHtml,
      optimisticSubtotal,
      getDrawerBody: () => drawerBody,
      getCartCountNodes: () => cartCountNodes,
      formatMoney,
    });

    // Legacy shims — DO NOT add new callers. Prefer OnixCart.bridge.*
    window.__onixOpenDrawer = openDrawer;
    window.__onixRefreshCart = safeRefreshCart;
    window.__onixBuildItemHtml = buildItemHtml;
    window.__onixOptimisticSubtotal = optimisticSubtotal;
    window.__onixDrawerBody = () => drawerBody;
    window.__onixCartCountNodes = () => cartCountNodes;
    window.__onixFormatMoney = formatMoney;

    /* Offer bundle optimistic inject: instant bundle-card inject for
       the offer popup. header.liquid scrapes title/image/size/variantId
       from its selected cards + the total bundle price, calls this helper,
       and the drawer shows the OFFRE DÉCOUVERTE card before the server
       round-trip even starts. refreshCart then replaces it with the real,
       server-keyed bundle card.

       @param {Array<{variantId:(number|string),title:string,image:string,url:string,size:string,fullUnitCents:number,displayCents:number}>} bundleItems
       @param {number} totalCents - bundle total in cents (shown in header). */
    window.__onixInjectOptimisticBundle = (bundleItems, totalCents) => {
      if (!drawerBody || !bundleItems || bundleItems.length === 0) return;
      const emptyState = drawerBody.querySelector('.onix-cart-drawer__empty');
      if (emptyState) drawerBody.innerHTML = '';
      /* Drop any existing bundle card so we never double-render. */
      const existing = drawerBody.querySelector('.onix-bundle-card');
      if (existing) existing.remove();

      /* Prefer explicit per-item cents from the caller (reflects the real
         discount reallocation — holder line cheap, others at full price).
         Fall back to an even split only if nothing was provided. */
      const evenSplit = Math.round((totalCents || 0) / bundleItems.length);
      const rowsHtml = bundleItems.map((it, idx) => {
        const itemCents = (typeof it.displayCents === 'number') ? it.displayCents : evenSplit;
        const displayKey = '__optimistic_bundle_' + idx + '_' + it.variantId;
        const media = it.image
          ? `<a class="onix-bundle-card__media" href="${escapeHtml(it.url || '#')}"><img src="${escapeHtml(it.image)}" alt="${escapeHtml(it.title || '')}"></a>`
          : `<div class="onix-bundle-card__media"></div>`;
        const sizePill = it.size
          ? `<span class="onix-bundle-card__size">${escapeHtml(it.size)}</span>`
          : '';
        return buildBundleRowHtml({
          displayKey,
          variantId: it.variantId,
          mediaHtml: media,
          url: it.url || '#',
          title: it.title || '',
          removeBtnHtml: `<button class="onix-bundle-card__remove" type="button" disabled aria-disabled="true">${BUNDLE_REMOVE_SVG}</button>`,
          sizePillHtml: sizePill,
          quantity: 1,
          priceCents: itemCents,
        });
      }).join('');

      const html = buildBundleSectionHtml(rowsHtml, totalCents || 0);

      drawerBody.insertAdjacentHTML('afterbegin', html);
      cartCountNodes.forEach(n => { n.textContent = Number(n.textContent) + bundleItems.length; });

      /* Race-prevention signal for the discount bar (and any other
         cart-counter listener). After we increment the count badge,
         observers fire and may read /cart.js in the brief window where
         Shopify has the items but not yet their `_offer_bundle`
         properties — making bundle items look like "regular" maillots
         and the bar flicker to a tier-reached fill before snapping
         back to 0%. The bar checks this hint to skip its update until
         properties land or the window expires. */
      window.__onixOptimisticBundleQty = bundleItems.length;
      window.__onixOptimisticBundleUntil = Date.now() + 5000;

      /* Subtotal instant sync: when injecting a bundle from an
         empty-cart state, the liquid-rendered subtotal is a plain money
         string — replace it with the animated span structure and set its
         text synchronously so the user sees the bundle total the same
         frame the bundle card appears. */
      if (subtotalNode) {
        const fullTotal = bundleItems.reduce((s, it) => s + (it.fullUnitCents || 0), 0);
        const hasCompare = fullTotal > (totalCents || 0);
        let subHtml = '<span data-subtotal-amount>' + formatMoney(totalCents || 0) + '</span>';
        if (hasCompare) {
          subHtml += '<span data-compare-amount class="onix-cart-drawer__subtotal-compare">' + formatMoney(fullTotal) + '</span>';
        }
        /* ⚠️ LE CONTENEUR `.onix-cart-drawer__subtotal-values` EST OBLIGATOIRE.
           C'est lui qui porte le `display: none` depuis le 17/08 — les montants
           sont dans le bouton, plus au-dessus. Ce chemin écrasait le conteneur
           en réécrivant `innerHTML`, et les deux montants réapparaissaient à
           l'écran, en double avec le bouton, uniquement après l'ajout d'un
           bundle. Le style en dur du prix barré est repassé en classe pour la
           même raison : en dur, il survivait au `display: none`. */
        subtotalNode.innerHTML =
          '<strong data-cart-subtotal class="onix-cart-drawer__subtotal-values">' + subHtml + '</strong>';
        __lastValues.subtotal = totalCents || 0;
        __lastValues.compare = hasCompare ? fullTotal : 0;
        /* Le bouton ne se mettait pas à jour ici : il gardait le total d'avant
           l'ajout du bundle jusqu'au rendu serveur suivant. */
        majTotalBouton(totalCents || 0, 250, hasCompare ? fullTotal : 0);
        majBoutonPaiement();
      }
      /* Discount UI stays visible — anti-stack handled by discount.js error. */
    };


  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCartDrawer, { once: true });
    return;
  }

  initCartDrawer();

})();
